import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { RunGuidanceOverlay } from './RunGuidanceOverlay';
// MiniRouteMap removed from live timer (FEB 13) — route visualization is post-workout only
import { VoiceGuidanceEngine } from '../services/voiceGuidanceEngine';
import { HeartRateTracker, type HeartRateData } from '../services/nativeHeartRateTracking';
import { EMOMTimerDisplay } from './EMOMTimerDisplay';
import { AMRAPTimerDisplay } from './AMRAPTimerDisplay';
import { DataService } from '../services/dataService'; // 🆕 PHASE 3: For marking cooldown skipped
import { expandPhasesForTimer } from '../utils/phaseGrouping';
import {
  getExerciseBasedPhaseProgress,
  getExerciseBoundary,
  getTimePerExercise,
} from '../services/workoutEngine/strengthProgress';
import { phaseProgressPct, AUTO_ADVANCE_PROGRESS_THRESHOLD } from '../services/workoutEngine/WorkoutEngine';
import { buildStrengthExercisesPayload } from '../utils/buildStrengthExercisesPayload';
import { weightUnitLabel, kgToDisplay, displayToKg, useWeightUnit } from '../utils/units';

interface Exercise {
  id: string;
  name: string;
  description: string;
  duration?: number;
  sets?: number;
  reps?: number;
  restPeriod?: number;
  instructions?: string; // String for detailed instructions
  form_cues?: string[]; // 🆕 FEB 3, 2026: Array of form cues for guidance drawer
  safety_notes?: string[]; // Safety tips for guidance drawer (form_cues fallback)
  images?: string[];
  circuit_info?: {
    circuit_number: number;
    round_number?: number;
    exercise_in_circuit: number;
    total_exercises_in_circuit: number;
    total_rounds?: number;
    is_circuit_start?: boolean;
    is_round_end?: boolean;
    is_circuit_end?: boolean;
    is_final_round?: boolean;
  };
}

interface GPSData {
  latitude: number;
  longitude: number;
  altitude?: number;
  accuracy: number;
  timestamp: number;
}

interface WorkoutData {
  id: string;
  workoutId: string;
  type: 'strength' | 'cardio' | 'mobility' | 'yoga';
  startTime: Date;
  endTime?: Date;
  totalDuration: number;
  exercises?: {
    exerciseId: string;
    duration: number;
    completed: boolean;
    sets?: number;
    reps?: number;
  }[];
  gpsData?: GPSData[];
  distance?: number;
  avgPace?: number;
  maxPace?: number;
  calories?: number;
  heartRate?: {
    avg?: number;
    max?: number;
  };
  notes?: string;
}

interface WorkoutPhase {
  name?: string; // Legacy field name
  phase_name?: string; // LLM-generated field name
  phase_type?: string; // LLM-generated field type (warmup, interval, recovery, cooldown, etc.)
  phase_order?: number; // LLM-generated phase order
  duration_seconds?: number;
  distance_meters?: number; // For distance-based phases
  exercises_json?: any[]; // For HIIT phases with exercise rotation
  target_pace_min_per_km?: string;
  target_pace_max_per_km?: string; // LLM generates both min and max
  target_pace?: string; // Legacy field
  target_hr_min?: number;
  target_hr_max?: number;
  instructions?: string; // LLM-generated guidance
  notes?: string; // Legacy field
  coaching_tips?: Array<{ type: 'pace' | 'form' | 'breathing' | 'motivation'; message: string }>; // Personalized timer toast cards
  repeat_count?: number; // Number of times to repeat this phase (e.g., 8 for 8x strides)
  rest_after_seconds?: number; // Recovery seconds between repetitions
  recovery_hr_target?: number; // Recovery HR ceiling BPM
  expansion?: { rep: number; repTotal: number; isRecovery: boolean; blockName: string };
}

interface UnifiedWorkoutTimerProps {
  workoutId: string;
  workoutTitle: string;
  workoutType: 'strength' | 'cardio' | 'mobility' | 'yoga';
  activitySubtype?: string; // 'running', 'cycling', 'walking' for cardio
  environment?: 'outdoor' | 'indoor' | 'mixed'; // 🗺️ GPS TRACKING: Determines if GPS is needed
  gpsRequired?: boolean; // Helper flag for GPS activation
  exercises?: Exercise[];
  phases?: WorkoutPhase[]; // 🆕 HIIT SUPPORT: Phase structure for specialized timers
  plannedDistance?: number;
  plannedDuration?: number;
  onComplete: (data: WorkoutData) => void;
  onDiscard: () => void;
  onExit: () => void;
  componentMobility?: any[]; // 🆕 PHASE 3: Warmup/cooldown mobility for two-option completion screen
  onMoveToCooldown?: (cooldownId: string) => void; // 🆕 PHASE 3: Navigate to cooldown
}

// 🆕 FEB 4, 2026 (Bug #76): EXERCISE-BASED PHASE PROGRESS HELPERS
// P2c (2026-06-07): these three helpers were extracted VERBATIM to
// services/workoutEngine/strengthProgress.ts (one source of truth, shared with
// the watch engine) and are now imported at the top of this file. Behavior is
// byte-identical to the prior inline copies — see strengthProgress.test.ts.

// P2c-R2 (2026-06-07): when true, cardio/mobility phase-advance uses the shared
// WorkoutEngine progress primitive (phaseProgressPct + AUTO_ADVANCE_PROGRESS_THRESHOLD)
// — the SAME decision the watch engine runs (golden-vector-tested), so phone + watch
// advance identically. Flip to false for an instant byte-identical revert to the legacy
// inline formula (removed in R3 after staging proves parity). Non-strength only; strength
// keeps the time-clamped exercise-progress path.
const USE_WORKOUT_ENGINE = true;

// Legacy helper for non-exercise-based progress (cardio, mobility)
const getPhaseProgress = (phase: WorkoutPhase, elapsedSeconds: number): number => {
  if (!phase.duration_seconds) return 0;
  return Math.min(100, (elapsedSeconds / phase.duration_seconds) * 100);
};

const getPhaseState = (
  phaseIndex: number, 
  currentPhaseIndex: number,
  phaseTime: number,
  phase: WorkoutPhase
): 'completed' | 'active' | 'upcoming' => {
  if (phaseIndex < currentPhaseIndex) return 'completed';
  if (phaseIndex === currentPhaseIndex) return 'active';
  return 'upcoming';
};

const formatPhaseName = (phase: WorkoutPhase): string => {
  return phase.phase_name || phase.name || 
    (phase.phase_type === 'activation' ? 'Activation' :
     phase.phase_type === 'main_compound' ? 'Main Workout' :
     phase.phase_type === 'accessory' ? 'Accessory' :
     phase.phase_type === 'cooldown' ? 'Cooldown' :
     `Phase ${phase.phase_order || ''}`);
};

const formatPhaseTime = (durationSeconds?: number): string => {
  if (!durationSeconds) return '';
  const minutes = Math.floor(durationSeconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    return `${hours}:${remainingMins.toString().padStart(2, '0')}`;
  }
  return `${minutes} min`;
};

// 🆕 JAN 29, 2026: TASK 1.2 - Smart Weight Display Helper
// Determines if exercise requires weight based on equipment and exercise type
const requiresWeight = (exercise: Exercise): boolean => {
  // Check images for equipment indicators
  if (exercise.images && exercise.images.length > 0) {
    const imageUrls = exercise.images.join(' ').toLowerCase();
    // If images mention dumbbells, barbells, kettlebells, or weights → requires weight
    if (imageUrls.includes('dumbbell') || 
        imageUrls.includes('barbell') || 
        imageUrls.includes('kettlebell') || 
        imageUrls.includes('weight')) {
      return true;
    }
  }
  
  // Check exercise name for weighted movements
  const exerciseName = exercise.name.toLowerCase();
  const weightedKeywords = [
    'press', 'row', 'curl', 'extension', 'raise', 'shrug', 
    'squat', 'deadlift', 'lunge', 'rdl', 'pull-up', 'chin-up'
  ];
  const bodyweightKeywords = [
    'plank', 'dead bug', 'bird dog', 'glute bridge', 'cat-cow',
    'hip switch', 'leg raise', 'mountain climber', 'push-up'
  ];
  
  // Bodyweight-only exercises
  if (bodyweightKeywords.some(kw => exerciseName.includes(kw))) {
    return false;
  }
  
  // Weighted exercises
  if (weightedKeywords.some(kw => exerciseName.includes(kw))) {
    return true;
  }
  
  // Default: Show weight input (user can leave blank if bodyweight)
  return true;
};

/** Renders a single warmup/cooldown exercise card with instructions + expandable form tips.
 *  Used in both warmup and cooldown stages of the web timer. */
const WebTimerExerciseCard: React.FC<{ exercise: any; index: number }> = ({ exercise, index }) => {
  const [tipsExpanded, setTipsExpanded] = useState(false);
  const formTips: string[] = exercise.form_cues || exercise.safety_notes || [];
  const instructions: string | undefined = typeof exercise.instructions === 'string' ? exercise.instructions : undefined;

  return (
    <div
      className="rounded-lg p-4"
      style={{
        background: 'var(--timer-card-bg)',
        border: '2px solid var(--timer-card-border)'
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0"
          style={{
            background: 'var(--timer-bg-tertiary)',
            color: 'var(--timer-text-cedar)'
          }}
        >
          {index + 1}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold" style={{ color: 'var(--timer-text-primary)' }}>
            {exercise.name}
          </h4>
          <p className="text-sm" style={{ color: 'var(--timer-text-tertiary)' }}>
            {exercise.duration || exercise.reps || '12 reps'}
          </p>
        </div>
        {exercise.images && exercise.images[0] && (
          <img
            src={exercise.images[0]}
            className="w-12 h-12 rounded object-cover flex-shrink-0"
            alt={exercise.name}
          />
        )}
      </div>

      {/* Instructions — always visible */}
      {instructions && (
        <p
          className="text-sm leading-relaxed mt-3"
          style={{ color: 'var(--timer-text-secondary)' }}
        >
          {instructions}
        </p>
      )}

      {/* Form Tips — expandable */}
      {formTips.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setTipsExpanded(p => !p)}
            className="text-xs font-semibold py-1"
            style={{ color: 'var(--timer-accent-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {tipsExpanded ? '▾' : '▸'} Form Tips ({formTips.length})
          </button>
          {tipsExpanded && (
            <ul className="mt-1 space-y-1 pl-3">
              {formTips.map((tip: string, i: number) => (
                <li key={i} className="flex items-start gap-2 text-xs leading-snug" style={{ color: 'var(--timer-accent-secondary)' }}>
                  <span className="flex-shrink-0 mt-0.5">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const UnifiedWorkoutTimer: React.FC<UnifiedWorkoutTimerProps> = ({
  workoutId,
  componentMobility = [], // 🆕 PHASE 3: Default to empty array
  onMoveToCooldown, // 🆕 PHASE 3: Cooldown navigation handler
  workoutTitle,
  workoutType,
  activitySubtype = 'running', // Default to running for cardio
  environment = 'outdoor', // Default to outdoor for backward compatibility
  gpsRequired = false,
  exercises = [],
  phases: rawPhases = [], // 🆕 HIIT SUPPORT: Phase structure
  onComplete,
  onDiscard,
  onExit
}) => {
  // 🆕 APR 7, 2026: Expand phases with repeat_count > 1 into individual work + recovery entries
  // Backend sends compact format (e.g., 1 stride phase with repeat_count:8).
  // Timer iterates linearly, so we unroll into 8 work + 7 recovery phases.
  const phases = useMemo(() => expandPhasesForTimer(rawPhases), [rawPhases]);

  // User units preference — controls kg vs lbs in the inline weight input.
  // DB stays canonical kg; we convert at the boundary.
  // Weight unit comes from the ONE shared resolver (utils/units.resolveWeightUnit).
  // Never read `profile.units` for a weight: the server deliberately does not
  // send it, so it falls back to device locale (imperial only for "US") and
  // shows KG to the 244/251 profiles whose stored weight_unit is 'lbs'.
  const units = useWeightUnit();

  // Timer state
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [totalTime, setTotalTime] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);

  // 🆕 HIIT Phase tracking
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseTime, setPhaseTime] = useState(0);
  // Refs for use inside setInterval closures where state may be stale
  const phasesRef = useRef(phases);
  const currentPhaseIndexRef = useRef(currentPhaseIndex);
  phasesRef.current = phases;
  currentPhaseIndexRef.current = currentPhaseIndex;
  const currentPhase = phases[currentPhaseIndex];
  // ✅ FIX: Check both phase_name and name fields
  const phaseType = (currentPhase?.phase_name || currentPhase?.name || currentPhase?.phase_type || '').toLowerCase();
  
  // Detect specialized HIIT timer modes
  const isEMOMMode = phaseType === 'emom';
  const isAMRAPMode = phaseType === 'amrap';
  const isCircuitMode = phaseType === 'circuit';
  
  // Exercise tracking (for strength/mobility)
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [exerciseTime, setExerciseTime] = useState(0);
  const [exerciseData, setExerciseData] = useState<WorkoutData['exercises']>([]);
  const [exerciseTimes, setExerciseTimes] = useState<number[]>([]);
  
  // 🆕 Strength-specific state
  const [actualWeights, setActualWeights] = useState<{[exerciseId: string]: number[]}>({});
  const [actualReps, setActualReps] = useState<{[exerciseId: string]: number[]}>({});
  const [isResting, setIsResting] = useState(false);
  const [restTimeRemaining, setRestTimeRemaining] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const restTimerRef = useRef<number | null>(null);
  
  // 🆕 FEB 2, 2026 (Bug #60): Guidance drawer state
  const [isGuidanceExpanded, setIsGuidanceExpanded] = useState(false);
  
  // 🆕 FEB 4, 2026 (Bug #75): Phase coaching drawer state (cardio)
  const [isPhaseGuidanceExpanded, setIsPhaseGuidanceExpanded] = useState(false);

  // 🆕 FEB 14, 2026: Rotating form tips state
  const [currentFormTipIndex, setCurrentFormTipIndex] = useState(0);

  // 🆕 FEB 14, 2026: Track actually completed sets per exercise (for set progress chips)
  const [setsCompletedForExercise, setSetsCompletedForExercise] = useState(0);
  
  // 🆕 FEB 4, 2026 (Multi-Stage): Stage tracking for warmup → main → cooldown
  // 🆕 FEB 5, 2026 (Bug #82): Detect warmup/cooldown and initialize stage correctly
  // 🔧 MAR 17, 2026: Tightened title matching to avoid false positives (e.g. "Recovery Run")
  //   and require actual pose/exercise content before showing a stage
  const mobilityHasContent = (m: any) => {
    const poses = m?.poses || m?.exercises_or_poses || m?.exercises || [];
    return poses.length > 0;
  };

  // 🆕 PHASE 11 SUB-SPRINT (APR 19, 2026) — Warmup/Cooldown Faithful Projection.
  // Structural `component_role` is the source of truth when present. Title-keyword
  // matching is the dual-read fallback for legacy rows only. Mirrors the native
  // timer's isWarmup/isCooldown helpers to keep web + native classification in sync.
  const warmupMobility = componentMobility?.find((m: any) => {
    // 🎯 Structural read first
    if (m.component_role === 'warmup') return true;
    if (m.component_role === 'cooldown') return false;
    // Fallback: title-keyword (legacy rows)
    const title = m.title?.toLowerCase() || '';
    return title.includes('warm') || title.includes('pre-') || title.includes('activation') || title.includes('prep') || title.includes('dynamic-stretching') || title.includes('dynamic stretching');
  });

  const cooldownMobility = componentMobility?.find((m: any) => {
    // 🎯 Structural read first
    if (m.component_role === 'cooldown') return true;
    if (m.component_role === 'warmup') return false;
    // Fallback: title-keyword (legacy rows)
    const title = m.title?.toLowerCase() || '';
    // Strong cooldown keywords — always match, never false-positive
    if (title.includes('cooldown') || title.includes('cool-down') || title.includes('cool down') || title.includes('post-')) {
      return true;
    }
    // Weak keywords (recovery/stretch) — only match if NOT paired with an activity type
    // e.g. "Recovery Stretch" = cooldown, but "Recovery Run" = cardio workout
    const activityWords = ['run', 'jog', 'swim', 'ride', 'cycle', 'walk', 'hike'];
    const hasActivityWord = activityWords.some(w => title.includes(w));
    if (hasActivityWord) return false;
    return title.includes('stretch') || title.includes('recovery');
  });

  // 🔧 MAR 17, 2026: Only show warmup/cooldown stages when they have actual exercise content
  const hasWarmup = !!warmupMobility && mobilityHasContent(warmupMobility);
  const hasCooldown = !!cooldownMobility && mobilityHasContent(cooldownMobility);

  // 🐛 BUG #82 FIX: Initialize to 'warmup' if warmup exists, otherwise 'main'
  const [currentStage, setCurrentStage] = useState<'warmup' | 'main' | 'cooldown'>(hasWarmup ? 'warmup' : 'main');
  const [warmupComplete, setWarmupComplete] = useState(false);
  const [mainWorkoutComplete, setMainWorkoutComplete] = useState(false);

  // 🔧 MAR 20, 2026: Diagnostic logging + stage sync when componentMobility changes
  // Fires once on mount and again only if componentMobility prop updates (not every tick)
  const stageDebugLoggedRef = React.useRef(false);
  React.useEffect(() => {
    // Log diagnostics once per componentMobility change
    if (!stageDebugLoggedRef.current || componentMobility?.length) {
      console.log('🔍 TIMER STAGE DEBUG:', {
        componentMobilityCount: componentMobility?.length || 0,
        componentMobilityTitles: componentMobility?.map((m: any) => m.title) || [],
        warmupFound: !!warmupMobility,
        warmupTitle: warmupMobility?.title,
        warmupPosesCount: warmupMobility?.poses?.length || 0,
        warmupExercisesCount: warmupMobility?.exercises?.length || 0,
        warmupHasContent: hasWarmup,
        cooldownFound: !!cooldownMobility,
        cooldownTitle: cooldownMobility?.title,
        cooldownPosesCount: cooldownMobility?.poses?.length || 0,
        cooldownExercisesCount: cooldownMobility?.exercises?.length || 0,
        hasCooldown,
        warmupRawKeys: warmupMobility ? Object.keys(warmupMobility) : [],
        warmupFirstPose: warmupMobility?.poses?.[0] || warmupMobility?.exercises?.[0] || 'none',
      });
      stageDebugLoggedRef.current = true;
    }

    // Sync stage if componentMobility arrived after initial mount
    if (hasWarmup && currentStage === 'main' && !warmupComplete && !isRunning) {
      console.log('🔧 TIMER STAGE SYNC: hasWarmup became true after mount, correcting stage to warmup');
      setCurrentStage('warmup');
    }
  }, [componentMobility]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 🆕 FEB 1, 2026: Hybrid auto/manual advancement for mobility/yoga
  const [advancementMode, setAdvancementMode] = useState<'auto' | 'manual'>('auto');
  const [showAutoAdvanceNotice, setShowAutoAdvanceNotice] = useState(false);
  
  // GPS tracking (for cardio)
  const [gpsData, setGpsData] = useState<GPSData[]>([]);
  const [distance, setDistance] = useState(0);
  const [currentPace, setCurrentPace] = useState(0);
  const [avgPace, setAvgPace] = useState(0);
  const [gpsPermission, setGpsPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  
  // 🆕 GPS Enhancements
  const [splits, setSplits] = useState<{km: number, time: number, pace: number, type: 'auto' | 'manual'}[]>([]);
  const [elevationGain, setElevationGain] = useState(0);
  const [elevationLoss, setElevationLoss] = useState(0);
  const [isAutoPaused, setIsAutoPaused] = useState(false);
  const [autoPauseEvents, setAutoPauseEvents] = useState<{startTime: number, endTime?: number}[]>([]);
  
  // 🆕 Voice Guidance
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const voiceEngineRef = useRef<VoiceGuidanceEngine | null>(null);

  // Voice helper: announce a phase with coaching-style for expanded reps, generic for others
  const announcePhaseVoice = useCallback((phase: WorkoutPhase) => {
    if (!voiceEngineRef.current) return;
    const ex = phase.expansion;
    if (ex) {
      if (ex.isRecovery) {
        const secs = phase.duration_seconds || 0;
        const durStr = secs >= 60
          ? `${Math.round(secs / 60)} minute${Math.round(secs / 60) !== 1 ? 's' : ''}`
          : `${secs} seconds`;
        voiceEngineRef.current.speak(`Recovery, ${durStr}`, 'high');
      } else {
        let msg = `${ex.blockName || 'Rep'} ${ex.rep} of ${ex.repTotal}, go!`;
        if (phase.duration_seconds && phase.duration_seconds < 60) msg += ` ${phase.duration_seconds} seconds`;
        voiceEngineRef.current.speak(msg, 'high');
      }
    } else {
      voiceEngineRef.current.announcePhaseTransition({
        phase_type: phase.phase_type || 'phase',
        phase_order: phase.phase_order || 1,
        target_pace: phase.target_pace_min_per_km || phase.target_pace,
        target_hr_min: phase.target_hr_min,
        target_hr_max: phase.target_hr_max,
        duration_seconds: phase.duration_seconds,
        distance_meters: phase.distance_meters,
      });
    }
  }, []);

  // 🆕 Heart Rate Tracking
  const [heartRateData, setHeartRateData] = useState<HeartRateData[]>([]);
  const [currentHR, setCurrentHR] = useState<number>(0);
  const [avgHR, setAvgHR] = useState<number>(0);
  const [maxHR, setMaxHR] = useState<number>(0);
  const [currentZone, setCurrentZone] = useState<number>(0);
  const [hrConnected, setHrConnected] = useState<boolean>(false);
  const hrTrackerRef = useRef<HeartRateTracker | null>(null);
  
  // UI state
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false); // 🆕 JAN 29, 2026: TASK 1.7 - Restart confirmation
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // 🆕 JAN 29, 2026: TASK 1.8 - Image gallery selection
  const [showCooldownTransition, setShowCooldownTransition] = useState(false); // 🆕 JAN 2026 PRIORITY 3: Two-option cooldown screen
  const [showSkipCooldownModal, setShowSkipCooldownModal] = useState(false); // 🆕 FEB 4 (Multi-Stage): Skip cooldown modal
  const [cooldownToSkip, setCooldownToSkip] = useState<any>(null); // 🆕 PHASE 3: Cooldown being skipped
  const [waitingForRunStart, setWaitingForRunStart] = useState(false); // 🔧 FEB 13: "I'm Ready" gate before main run phases begin
  
  const timerRef = useRef<number | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const isPausedRef = useRef(false);
  const isAutoPausedRef = useRef(false);
  const lastPositionRef = useRef<GPSData | null>(null);
  const lastAltitudeRef = useRef<number | null>(null);
  const lastSpeedCheckRef = useRef<number>(Date.now());
  const lowSpeedDurationRef = useRef<number>(0);
  const contentRef = useRef<HTMLDivElement | null>(null); // 🆕 Ref for scrollable content area
  
  // Lock body scroll when timer is open (prevents background page scroll)
  useEffect(() => {
    // Save original body overflow
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Scroll content to top on mount
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Initialize voice guidance engine
  useEffect(() => {
    voiceEngineRef.current = new VoiceGuidanceEngine({
      enabled: voiceEnabled,
      periodicUpdateInterval: 500000 // 500m
    });
    
    return () => {
      voiceEngineRef.current?.stopAll();
    };
  }, []);
  
  // Update voice enabled state
  useEffect(() => {
    voiceEngineRef.current?.setEnabled(voiceEnabled);
  }, [voiceEnabled]);

  // 🆕 Rotating form tips: auto-cycle every 5s during active exercise
  useEffect(() => {
    setCurrentFormTipIndex(0); // Reset on exercise change
    const tips = exercises[currentExerciseIndex]?.form_cues || exercises[currentExerciseIndex]?.safety_notes || [];
    if (tips.length <= 1 || !isRunning) return;

    const interval = setInterval(() => {
      setCurrentFormTipIndex(prev => (prev + 1) % tips.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [currentExerciseIndex, isRunning, exercises]);

  // 🆕 FEB 14: Auto-advance to next exercise after inter-exercise rest ends
  const wasRestingRef = useRef(false);

  useEffect(() => {
    if (isResting) {
      wasRestingRef.current = true;
    } else if (wasRestingRef.current) {
      // Rest just ended — check if current exercise is fully complete
      wasRestingRef.current = false;

      const currentEx = exercises[currentExerciseIndex];
      const totalSets = currentEx?.sets || 0;

      if (
        workoutType === 'strength' &&
        totalSets > 0 &&
        setsCompletedForExercise >= totalSets &&
        currentExerciseIndex < exercises.length - 1
      ) {
        nextExercise();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResting, setsCompletedForExercise, currentExerciseIndex, workoutType, exercises]);

  // 🆕 Initialize heart rate tracker with user's actual zone boundaries
  useEffect(() => {
    const initHrTracker = async () => {
      let maxHR = 185; // Fallback
      let zones = { zone1Max: 0.6, zone2Max: 0.7, zone3Max: 0.8, zone4Max: 0.9 };

      try {
        const userZones = await DataService.getCachedHeartRateZones();
        if (userZones && userZones.maxHeartRate) {
          maxHR = userZones.maxHeartRate;
          // Convert BPM boundaries to percentage thresholds for HeartRateTracker
          zones = {
            zone1Max: userZones.zone1Max / maxHR,
            zone2Max: userZones.zone2Max / maxHR,
            zone3Max: userZones.zone3Max / maxHR,
            zone4Max: userZones.zone4Max / maxHR,
          };
        }
      } catch { /* non-critical — use defaults */ }

      hrTrackerRef.current = new HeartRateTracker({
        maxHR,
        zones,
      });
    };

    initHrTracker();

    return () => {
      hrTrackerRef.current?.disconnect();
    };
  }, []);

  // 🆕 Connect to heart rate monitor
  const connectHeartRate = async () => {
    try {
      if (!HeartRateTracker.isSupported()) {
        alert('Bluetooth heart rate monitors are not supported in this browser');
        return;
      }
      
      await hrTrackerRef.current?.connect();
      setHrConnected(true);
      
      // Register callback for HR updates
      hrTrackerRef.current?.onHeartRateUpdate((data: HeartRateData) => {
        setCurrentHR(data.bpm);
        setCurrentZone(data.zone || 0);
        setHeartRateData(prev => [...prev, data]);
        
        // Calculate average HR
        setAvgHR(prevAvg => {
          const total = prevAvg * (heartRateData.length) + data.bpm;
          return total / (heartRateData.length + 1);
        });
        
        // Track max HR
        setMaxHR(prev => Math.max(prev, data.bpm));
        
        // Voice warning for high HR (zone 5)
        if (data.zone === 5) {
          voiceEngineRef.current?.announceHeartRateWarning(data.bpm, 185, 5);
        }
      });
    } catch (error) {
      console.error('Failed to connect to HR monitor:', error);
      alert('Failed to connect to heart rate monitor');
    }
  };

  // 🆕 Disconnect heart rate monitor
  const disconnectHeartRate = async () => {
    await hrTrackerRef.current?.disconnect();
    setHrConnected(false);
  };

  // Timer logic
  useEffect(() => {
    if (isRunning && !isPaused) {
      timerRef.current = window.setInterval(() => {
        setTotalTime(prev => prev + 1);
        
          // 🆕 HIIT: Update phase time for EMOM/AMRAP timers
              // Only increment phaseTime during 'main' stage AND after user taps "I'm Ready"
              if (phases.length > 0 && currentStage === 'main' && !waitingForRunStart) {
                setPhaseTime(prev => prev + 1);
              }
        
        if (workoutType !== 'cardio') {
          setExerciseTime(prev => prev + 1);
          // Update the current exercise's time in the array
          setExerciseTimes(prevTimes => {
            const newTimes = [...prevTimes];
            newTimes[currentExerciseIndex] = (newTimes[currentExerciseIndex] || 0) + 1;
            return newTimes;
          });
        }
      }, 1000);
    } else {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
      if (restTimerRef.current) {
        window.clearInterval(restTimerRef.current);
        restTimerRef.current = null;
      }
    };
  }, [isRunning, isPaused, workoutType, currentExerciseIndex, currentStage]);

  // 🆕 FEB 4, 2026 (Bug #76): AUTOMATIC PHASE PROGRESSION for ALL workout types
  // 🔧 FEB 13: Only run phase progression during 'main' stage (not warmup/cooldown)
  useEffect(() => {
    if (currentStage !== 'main') return;
    if (waitingForRunStart) return; // 🔧 FEB 13: Don't advance phases until user taps "I'm Ready"
    if (phases.length > 0 && currentPhase && currentPhase.duration_seconds) {
      // Check if phase complete (100% progress reached)
      const progress = workoutType === 'strength'
        ? getExerciseBasedPhaseProgress(phaseTime, currentExerciseIndex, exercises.length, currentPhase.duration_seconds)
        : USE_WORKOUT_ENGINE
          ? phaseProgressPct(currentPhase.duration_seconds, phaseTime)
          : (phaseTime / currentPhase.duration_seconds) * 100;
      
      // 99.9 === AUTO_ADVANCE_PROGRESS_THRESHOLD (shared with the watch engine).
      if (progress >= (USE_WORKOUT_ENGINE ? AUTO_ADVANCE_PROGRESS_THRESHOLD : 99.9)) { // float tolerance
        // Phase completed - advance to next phase
        if (currentPhaseIndex < phases.length - 1) {
          const nextPhaseIndex = currentPhaseIndex + 1;
          const nextPhase = phases[nextPhaseIndex];
          const nextPhaseName = nextPhase.phase_name || 
            (nextPhase.phase_type === 'warmup' ? 'Warmup' :
             nextPhase.phase_type === 'cooldown' ? 'Cooldown' :
             nextPhase.phase_type === 'recovery' ? 'Rest' :
             nextPhase.phase_type === 'interval' ? 'Run' :
             nextPhase.phase_type === 'tempo' ? 'Tempo Run' :
             `Phase ${nextPhaseIndex + 1}`);
          
          // Announce phase change
          announcePhaseVoice(nextPhase);
          
          // Advance to next phase
          setCurrentPhaseIndex(nextPhaseIndex);
          setPhaseTime(0);
          
          console.log(`✅ Phase ${currentPhaseIndex + 1} complete. Advancing to: ${nextPhaseName}`);
        } else {
          // Last phase completed - could auto-complete workout or just continue
          console.log(`✅ All phases complete!`);
        }
      }
    }
  }, [phaseTime, currentPhase, currentPhaseIndex, phases, workoutType, currentExerciseIndex, exercises.length, currentStage]);

  // 🗺️ GPS tracking for OUTDOOR cardio workouts ONLY
  useEffect(() => {
    // Only activate GPS for outdoor cardio activities (running, cycling)
    // GPS must only track during the 'main' workout stage — NOT during warmup or cooldown
    const isOutdoorCardio = workoutType === 'cardio' && 
                           environment === 'outdoor' &&
                           (activitySubtype === 'running' || activitySubtype === 'cycling');
    
    if (isOutdoorCardio && isRunning && !isPaused && currentStage === 'main' && !waitingForRunStart) {
      startGPSTracking();
    } else {
      stopGPSTracking();
    }

    return () => stopGPSTracking();
  }, [workoutType, environment, activitySubtype, isRunning, isPaused, currentStage, waitingForRunStart]);

  const startGPSTracking = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    // Clear existing watch before starting a new one to prevent leak
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' }).then(result => {
        setGpsPermission(result.state as 'granted' | 'denied' | 'prompt');
      }).catch(() => {
        // permissions.query not supported (iOS Safari/PWA) — proceed without status
      });
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 1000
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        // Guard: skip GPS updates if workout is paused (ref avoids stale closure)
        if (isPausedRef.current) return;

        const newPoint: GPSData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          altitude: position.coords.altitude || undefined,
          accuracy: position.coords.accuracy,
          timestamp: Date.now()
        };

        setGpsAccuracy(position.coords.accuracy);
        setGpsData(prev => [...prev, newPoint]);

        // Calculate distance and pace
        if (lastPositionRef.current) {
          const distanceIncrement = calculateDistance(
            lastPositionRef.current.latitude,
            lastPositionRef.current.longitude,
            newPoint.latitude,
            newPoint.longitude
          );
          
          const timeIncrement = (newPoint.timestamp - lastPositionRef.current.timestamp) / 1000;
          const currentSpeed = timeIncrement > 0 ? distanceIncrement / timeIncrement : 0; // meters per second
          
          // 🆕 Auto-Pause Detection
          checkAutoPause(currentSpeed);
          
          if (!isAutoPausedRef.current) {
            setDistance(prev => {
              const newDistance = prev + distanceIncrement;
              let segmentPace = currentPace; // 🐛 BUG #80 FIX: Declare outside if block for scope
              
              // Calculate current pace (time for last segment)
              if (timeIncrement > 0 && distanceIncrement > 0) {
                segmentPace = timeIncrement / (distanceIncrement / 1000); // seconds per km
                setCurrentPace(segmentPace);
                
                // Update average pace
                if (newDistance > 0) {
                  setAvgPace(totalTime / (newDistance / 1000));
                }
              }
              
              // 🆕 Auto-generate splits every kilometer
              const currentKm = Math.floor(newDistance / 1000);
              const lastSplitKm = splits.length;
              if (currentKm > lastSplitKm) {
                const splitTime = totalTime;
                const splitPace = totalTime / currentKm; // Average pace for this km
                
                setSplits(prev => [...prev, {
                  km: currentKm,
                  time: splitTime,
                  pace: splitPace,
                  type: 'auto'
                }]);
                
                // 🆕 Voice announcement for split
                voiceEngineRef.current?.announceSplit(currentKm, splitTime, splitPace);
                console.log(`Split ${currentKm}: ${formatPace(splitPace)}`);
              }
              
              // 🆕 Periodic updates every 500m (with HR if available)
              voiceEngineRef.current?.announcePeriodicUpdate(newDistance, segmentPace, currentHR > 0 ? currentHR : undefined);
              
              return newDistance;
            });
          }
          
          // 🆕 Elevation Tracking
          if (newPoint.altitude && lastAltitudeRef.current !== null) {
            const altChange = newPoint.altitude - lastAltitudeRef.current;
            
            // 3m threshold filters GPS noise
            if (altChange > 3) {
              setElevationGain(prev => prev + altChange);
            } else if (altChange < -3) {
              setElevationLoss(prev => prev + Math.abs(altChange));
            }
          }
          
          if (newPoint.altitude) {
            lastAltitudeRef.current = newPoint.altitude;
          }
        }

        lastPositionRef.current = newPoint;
      },
      (error) => {
        console.error('GPS error:', error);
        setGpsPermission('denied');
      },
      options
    );
  }, [totalTime]);

  const stopGPSTracking = useCallback(() => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  // Haversine formula for distance calculation
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // 🆕 Auto-Pause Detection
  const checkAutoPause = useCallback((currentSpeed: number) => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastSpeedCheckRef.current;
    
    // Speed threshold: 0.5 m/s (roughly 1.8 km/h or 1.1 mph)
    const SPEED_THRESHOLD = 0.5;
    const PAUSE_DURATION_THRESHOLD = 5000; // 5 seconds
    
    if (currentSpeed < SPEED_THRESHOLD) {
      lowSpeedDurationRef.current += timeSinceLastCheck;
      
      // Auto-pause after 5 seconds of low speed
      if (lowSpeedDurationRef.current >= PAUSE_DURATION_THRESHOLD && !isAutoPaused) {
        isAutoPausedRef.current = true;
        setIsAutoPaused(true);
        setAutoPauseEvents(prev => [...prev, { startTime: Date.now() }]);
        console.log('Auto-paused: Speed below threshold');
      }
    } else {
      // Resume if speed increases
      if (isAutoPaused) {
        isAutoPausedRef.current = false;
        setIsAutoPaused(false);
        setAutoPauseEvents(prev => {
          const updated = [...prev];
          if (updated.length > 0 && !updated[updated.length - 1].endTime) {
            updated[updated.length - 1].endTime = Date.now();
          }
          return updated;
        });
        console.log('Auto-resumed: Speed above threshold');
      }
      lowSpeedDurationRef.current = 0;
    }
    
    lastSpeedCheckRef.current = now;
  }, [isAutoPaused]);

  // 🆕 Manual Lap Button Handler
  const handleManualLap = useCallback(() => {
    const currentKm = distance / 1000;
    const splitTime = totalTime;
    const splitPace = totalTime / currentKm;
    
    setSplits(prev => [...prev, {
      km: currentKm,
      time: splitTime,
      pace: splitPace,
      type: 'manual'
    }]);
    
    console.log(`Manual lap at ${currentKm.toFixed(2)}km`);
  }, [distance, totalTime]);

  // Helper to format pace (seconds per km to MM:SS format)
  const formatPace = (secondsPerKm: number): string => {
    if (!secondsPerKm || !isFinite(secondsPerKm)) return '--:--';
    const minutes = Math.floor(secondsPerKm / 60);
    const seconds = Math.floor(secondsPerKm % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Helper to format time (seconds to HH:MM:SS or MM:SS format)
  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const startWorkout = () => {
    setIsRunning(true);
    setIsPaused(false);
    setStartTime(new Date());
    
    // 🆕 Initialize phase tracking for cardio workouts
    // 🔧 FEB 13: Only init phases during 'main' stage (warmup/cooldown don't use run phases)
    if (workoutType === 'cardio' && phases.length > 0 && currentStage === 'main') {
      setCurrentPhaseIndex(0);
      setPhaseTime(0);
      
      // Announce first phase
      if (phases[0]) announcePhaseVoice(phases[0]);
    }

    if (workoutType !== 'cardio' && exercises.length > 0) {
      // Initialize exercise tracking
      setExerciseData(exercises.map(ex => ({
        exerciseId: ex.id,
        duration: 0,
        completed: false,
        sets: ex.sets,
        reps: ex.reps
      })));
      
      // Initialize exercise times array
      setExerciseTimes(new Array(exercises.length).fill(0));

      // 🆕 FEB 14, 2026: Voice announce first exercise for mobility/yoga
      if ((workoutType === 'mobility' || workoutType === 'yoga') && voiceEngineRef.current && voiceEnabled) {
        const firstEx = exercises[0];
        if (firstEx) {
          const poseName = firstEx.name || 'First pose';
          const dur = firstEx.duration ? `, hold for ${firstEx.duration} seconds` : '';
          const cue = firstEx.form_cues?.[0] ? `. ${firstEx.form_cues[0]}` : '';
          voiceEngineRef.current.speak(`Starting workout. ${poseName}. 1 of ${exercises.length}${dur}${cue}`, 'high');
        }
      }
    }
  };

  const pauseWorkout = () => {
    isPausedRef.current = true;
    setIsPaused(true);
  };

  const resumeWorkout = () => {
    isPausedRef.current = false;
    setIsPaused(false);
  };

  const nextExercise = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      // 🔧 FIX (Apr 14, 2026): Capture next index in local variable BEFORE React
      // state batching. Previously used `currentExerciseIndex + 1` in closures after
      // `setCurrentExerciseIndex(prev => prev + 1)`, but the closure captures the OLD
      // index (React batches state updates). This caused exerciseTime to load the
      // wrong exercise's stored time on transition.
      const nextIdx = currentExerciseIndex + 1;
      const nextEx = exercises[nextIdx];

      // Mark current exercise as completed
      setExerciseData(prev => prev?.map((ex, idx) =>
        idx === currentExerciseIndex
          ? { ...ex, duration: exerciseTimes[currentExerciseIndex] || 0, completed: true }
          : ex
      ) || []);

      setCurrentExerciseIndex(nextIdx);
      setCurrentFormTipIndex(0);
      setCurrentSet(1);
      setSetsCompletedForExercise(0);
      setExerciseTime(exerciseTimes[nextIdx] || 0);

      // Voice announce next exercise (manual mode — auto mode announces via its own useEffect)
      if ((workoutType === 'mobility' || workoutType === 'yoga') && advancementMode === 'manual' && voiceEngineRef.current && voiceEnabled) {
        if (nextEx) {
          const poseName = nextEx.name || 'Next pose';
          const dur = nextEx.duration ? `, hold for ${nextEx.duration} seconds` : '';
          const cue = nextEx.form_cues?.[0] ? `. ${nextEx.form_cues[0]}` : '';
          voiceEngineRef.current.speak(`${poseName}. ${nextIdx + 1} of ${exercises.length}${dur}${cue}`, 'high');
        }
      }
    }
  };

  const previousExercise = () => {
    if (currentExerciseIndex > 0) {
      const prevIdx = currentExerciseIndex - 1;
      const prevEx = exercises[prevIdx];

      setCurrentExerciseIndex(prevIdx);
      setCurrentFormTipIndex(0);
      setExerciseTime(exerciseTimes[prevIdx] || 0);
      setCurrentSet(1);
      setSetsCompletedForExercise(0);

      // Voice announce when going back to previous exercise
      if ((workoutType === 'mobility' || workoutType === 'yoga') && voiceEngineRef.current && voiceEnabled) {
        if (prevEx) {
          const poseName = prevEx.name || 'Previous pose';
          voiceEngineRef.current.speak(`Going back. ${poseName}. ${prevIdx + 1} of ${exercises.length}`, 'high');
        }
      }
    }
  };

  // 🆕 FEB 1, 2026: Handle advancement mode change (with persistence)
  const handleAdvancementModeChange = async (mode: 'auto' | 'manual') => {
    setAdvancementMode(mode);
    try {
      const { storage } = await import('../services/storage');
      await storage.setItem(`advancement_mode_${workoutType}`, mode);
    } catch (error) {
      console.warn('Failed to save advancement preference', error);
    }
  };

  // 🆕 Weight/Reps Logging
  const logSetWeight = (exerciseId: string, setIndex: number, weight: number) => {
    setActualWeights(prev => {
      const exerciseWeights = [...(prev[exerciseId] || [])];
      exerciseWeights[setIndex] = weight;
      return { ...prev, [exerciseId]: exerciseWeights };
    });
  };

  const logSetReps = (exerciseId: string, setIndex: number, reps: number) => {
    setActualReps(prev => {
      const exerciseReps = [...(prev[exerciseId] || [])];
      exerciseReps[setIndex] = reps;
      return { ...prev, [exerciseId]: exerciseReps };
    });
  };

  // 🆕 Enhanced Rest Timer
  const startRestTimer = (duration: number) => {
    setIsResting(true);
    setRestTimeRemaining(duration);
    
    restTimerRef.current = window.setInterval(() => {
      setRestTimeRemaining(prev => {
        if (prev <= 1) {
          endRestTimer();
          // Vibrate at end (if supported)
          if ('vibrate' in navigator) {
            navigator.vibrate(200);
          }
          
          // 🐛 BUG #75 FIX + Apr 14 2026 clamp: Update phase time when rest
          // completes, but clamp to the current phase's total duration to prevent
          // overshoot that could auto-fire the next exercise mid-rep.
          setPhaseTime(prevPhaseTime => {
            const next = prevPhaseTime + duration;
            // Use refs to avoid stale closure — phases/currentPhaseIndex may have
            // changed during the rest timer interval (React batching)
            const phaseDuration = phasesRef.current[currentPhaseIndexRef.current]?.duration_seconds;
            return phaseDuration ? Math.min(next, phaseDuration) : next;
          });
          
          return 0;
        }
        
        // Vibrate at 5 seconds remaining
        if (prev === 5 && 'vibrate' in navigator) {
          navigator.vibrate([100, 100, 100]);
        }
        
        return prev - 1;
      });
    }, 1000);
  };

  const endRestTimer = () => {
    if (restTimerRef.current) {
      window.clearInterval(restTimerRef.current);
      restTimerRef.current = null;
    }
    setIsResting(false);
    setRestTimeRemaining(0);
  };

  const adjustRestTime = (seconds: number) => {
    setRestTimeRemaining(prev => Math.max(0, prev + seconds));
    // Phase time already at boundary, no adjustment needed
  };

  const skipRest = () => {
    // Rest skipped → Just end timer (phase time already at boundary)
    endRestTimer();
  };
  
  // 🆕 FEB 4, 2026 (Multi-Stage): Stage transition functions
  const advanceToMainWorkout = () => {
    console.log('🔥 → 🏃 STAGE TRANSITION: Warmup → Main Workout (waiting for "I\'m Ready")');
    setWarmupComplete(true);
    setCurrentStage('main');
    setIsPaused(false);
    // 🔧 FEB 13: Keep timer running — totalTime stays cumulative
    // BUT don't start phases/GPS/voice yet — wait for user's "I'm Ready" tap
    setWaitingForRunStart(true);

    // Reset phase tracking (will be activated by startMainRun)
    if (phases.length > 0) {
      setCurrentPhaseIndex(0);
      setPhaseTime(0);
    }
  };

  // 🔧 FEB 13: User taps "I'm Ready" — NOW start phases, GPS, voice guidance
  const startMainRun = () => {
    console.log('🏃 USER READY: Starting main run phases + GPS + voice');
    setWaitingForRunStart(false);

    // Announce the first phase now that user is ready
    if (phases[0]) announcePhaseVoice(phases[0]);
  };
  
  const advanceToCooldown = () => {
    console.log('🏃 → ❄️ STAGE TRANSITION: Main Workout → Cooldown');
    setMainWorkoutComplete(true);
    setCurrentStage('cooldown');
    setIsPaused(false);
    // 🔧 FEB 13: Keep timer running — no extra GO! press needed
    // totalTime stays cumulative
    // Reset phase tracking (cooldown is mobility, not run phases)
    setCurrentPhaseIndex(0);
    setPhaseTime(0);
  };
  
  const completeCooldown = () => {
    console.log('✅ ALL STAGES COMPLETE: Warmup → Main → Cooldown');
    // Build complete workout data and call parent completion
    const fullWorkoutData: WorkoutData = {
      id: `session_${Date.now()}`,
      workoutId,
      type: workoutType,
      startTime: startTime!,
      endTime: new Date(),
      totalDuration: totalTime,
      // Include all collected data
      ...(workoutType === 'cardio' ? {
        gpsData,
        distance: distance / 1000,
        avgPace,
        heartRate: heartRateData.length > 0 ? { avg: Math.round(avgHR), max: maxHR } : undefined
      } : {})
    };
    
    onComplete(fullWorkoutData);
  };
  
  // 🆕 BUG #76: Skip to next exercise (replaces Previous button)
  const skipToNextExercise = () => {
    if (!isRunning) {
      console.warn('⚠️ Cannot skip - workout not started. Press GO! first.');
      return;
    }
    
    if (currentExerciseIndex >= exercises.length - 1) {
      console.warn('⚠️ Cannot skip - already on last exercise.');
      return;
    }
    
    // 🆕 Jump phase time to next exercise boundary
    if (phases.length > 0 && currentPhase && currentPhase.duration_seconds) {
      const timePerExercise = getTimePerExercise(currentPhase.duration_seconds, exercises.length);
      const nextBoundary = getExerciseBoundary(currentExerciseIndex + 1, timePerExercise);
      
      console.log(`⏭️ SKIP: Phase time ${phaseTime}s → ${nextBoundary}s (Exercise ${currentExerciseIndex + 2} boundary)`);
      setPhaseTime(nextBoundary);
    }
    
    // Advance to next exercise
    nextExercise();
    setCurrentSet(1);
    setSetsCompletedForExercise(0); // Reset completed sets tracker
    
    // Clear any active rest
    if (isResting) {
      endRestTimer();
    }
  };

  const completeSet = () => {
    // 🐛 BUG #76: Prevent completing sets before GO! is pressed
    if (!isRunning) {
      console.warn('⚠️ Cannot complete set - workout not started. Press GO! first.');
      return;
    }
    
    // 🆕 FEB 14: Track completed sets for progress chips
    setSetsCompletedForExercise(prev => prev + 1);
    
    const currentExercise = exercises[currentExerciseIndex];
    if (currentExercise && currentExercise.sets && currentSet < currentExercise.sets) {
      // More sets remaining - start rest (stay in same exercise)
      startRestTimer(currentExercise.restPeriod || 90);
      setCurrentSet(prev => prev + 1);
      
      // ❌ DON'T update phase time (still in same exercise)
    } else {
      // Last set complete - exercise done!
      
      // 🆕 BUG #76: Jump phase time to exercise boundary
      if (phases.length > 0 && currentPhase && currentPhase.duration_seconds) {
        const timePerExercise = getTimePerExercise(currentPhase.duration_seconds, exercises.length);
        const nextBoundary = getExerciseBoundary(currentExerciseIndex + 1, timePerExercise);
        
        console.log(`✅ Exercise ${currentExerciseIndex + 1} complete - Phase time: ${phaseTime}s → ${nextBoundary}s`);
        setPhaseTime(nextBoundary);
      }
      
      // Start rest before next exercise (or just advance if last exercise)
      if (currentExerciseIndex < exercises.length - 1) {
        startRestTimer(currentExercise.restPeriod || 90);
      } else {
        // Last exercise - advance without rest
        nextExercise();
        setCurrentSet(1);
      }
    }
  };

  const completeWorkout = () => {
    setShowCompleteConfirm(true);
  };

  const confirmComplete = () => {
    // 🆕 JAN 2026 PRIORITY 3: Build workout data and call onComplete
    // Note: Modal already handles cooldown transition screen (lines 1603-1693)
    const workoutData: WorkoutData & {
      splits?: typeof splits;
      elevationGain?: number;
      elevationLoss?: number;
      autoPauseEvents?: typeof autoPauseEvents;
      heartRateData?: typeof heartRateData;
    } = {
      id: `session_${Date.now()}`,
      workoutId,
      type: workoutType,
      startTime: startTime!,
      endTime: new Date(),
      totalDuration: totalTime,
      ...(workoutType === 'cardio' ? {
        gpsData,
        distance: distance / 1000, // Convert to km
        avgPace,
        maxPace: currentPace,
        calories: Math.round((distance / 1000) * 65), // Rough estimate
        // 🆕 GPS Enhancements Data
        splits: splits.length > 0 ? splits : undefined,
        elevationGain: elevationGain > 0 ? elevationGain : undefined,
        elevationLoss: elevationLoss > 0 ? elevationLoss : undefined,
        autoPauseEvents: autoPauseEvents.length > 0 ? autoPauseEvents : undefined,
        // 🆕 Heart Rate Data
        heartRate: heartRateData.length > 0 ? {
          avg: Math.round(avgHR),
          max: maxHR
        } : undefined,
        heartRateData: heartRateData.length > 0 ? heartRateData : undefined
      } : {
        exercises: buildStrengthExercisesPayload(exerciseData, exerciseTimes),
        // 🆕 FEB 14: Include logged weights and reps for non-cardio workouts
        actualWeights: Object.keys(actualWeights).length > 0 ? actualWeights : undefined,
        actualReps: Object.keys(actualReps).length > 0 ? actualReps : undefined,
        // 🆕 Heart Rate Data (for strength workouts too)
        heartRate: heartRateData.length > 0 ? {
          avg: Math.round(avgHR),
          max: maxHR
        } : undefined,
        heartRateData: heartRateData.length > 0 ? heartRateData : undefined
      })
    };

    onComplete(workoutData);
  };

  const discardWorkout = () => {
    setShowDiscardConfirm(true);
  };

  const confirmDiscard = () => {
    stopGPSTracking();
    setIsRunning(false);
    setShowDiscardConfirm(false);
    onDiscard();
  };

  // 🆕 JAN 2026 PRIORITY 3: Handle Move to Cooldown
  const handleMoveToCooldownClick = () => {
    if (!cooldownToSkip || !onMoveToCooldown) return;
    
    console.log('🏁 Moving to cooldown:', cooldownToSkip.title);
    
    // Build workout data for completion
    const workoutData: WorkoutData = {
      id: `session_${Date.now()}`,
      workoutId,
      type: workoutType,
      startTime: startTime!,
      endTime: new Date(),
      totalDuration: totalTime
    };
    
    // Close transition screen
    setShowCooldownTransition(false);
    
    // Call completion to mark workout as done
    onComplete(workoutData);
    
    // Navigate to cooldown (parent will handle navigation)
    onMoveToCooldown(cooldownToSkip.id);
  };
  
  // 🆕 PHASE 3.4: Handle Skip Cooldown
  const handleSkipCooldown = async (reason: string) => {
    if (!cooldownToSkip) return;
    
    try {
      // Mark cooldown as skipped via DataService
      await DataService.markMobilitySkipped(cooldownToSkip.id, {
        skip_reason: reason,
        parent_workout_id: workoutId
      });
      
      console.log('✅ Cooldown skipped:', cooldownToSkip.title, 'Reason:', reason);
      
      // Close modals
      setShowSkipCooldownModal(false);
      setShowCooldownTransition(false);
      setCooldownToSkip(null);
      
      // Build workout data and complete
      const workoutData: WorkoutData = {
        id: `session_${Date.now()}`,
        workoutId,
        type: workoutType,
        startTime: startTime!,
        endTime: new Date(),
        totalDuration: totalTime
      };
      
      onComplete(workoutData);
    } catch (error) {
      console.error('❌ Error skipping cooldown:', error);
      // Still complete the workout even if skip tracking fails
      const workoutData: WorkoutData = {
        id: `session_${Date.now()}`,
        workoutId,
        type: workoutType,
        startTime: startTime!,
        endTime: new Date(),
        totalDuration: totalTime
      };
      onComplete(workoutData);
    }
  };

  // 🆕 FEB 4, 2026 (Multi-Stage): Handle Skip Cooldown with Reason (New Multi-Stage Flow)
  const handleSkipCooldownWithReason = async (reason: string) => {
    if (cooldownMobility) {
      try {
        await DataService.markMobilitySkipped(cooldownMobility.id, {
          skip_reason: reason,
          parent_workout_id: workoutId
        });
        console.log('✅ Cooldown skipped (multi-stage):', cooldownMobility.title, 'Reason:', reason);
      } catch (error) {
        console.error('❌ Error marking cooldown skipped:', error);
      }
    }
    
    setShowSkipCooldownModal(false);
    completeCooldown(); // Complete workout after skipping
  };
  
  // 🆕 JAN 29, 2026: TASK 1.7 - Restart Workout Handler
  const restartWorkout = () => {
    setShowRestartConfirm(true);
  };

  const confirmRestart = () => {
    // Stop all timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (restTimerRef.current) {
      clearInterval(restTimerRef.current);
      restTimerRef.current = null;
    }
    
    // Stop GPS tracking
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    // Stop heart rate monitoring
    hrTrackerRef.current?.disconnect();
    
    // Reset ALL state to initial values
    setIsRunning(false);
    setIsPaused(false);
    setTotalTime(0);
    setStartTime(null);
    setCurrentExerciseIndex(0);
    setCurrentFormTipIndex(0); // Reset form tip on restart
    setExerciseTime(0);
    setExerciseData([]);
    setExerciseTimes([]);
    setCurrentSet(1);
    setSetsCompletedForExercise(0); // Reset completed sets tracker
    setIsResting(false);
    setRestTimeRemaining(0);
    setActualWeights({});
    setActualReps({});
    
    // Reset GPS data
    setGpsData([]);
    setDistance(0);
    setCurrentPace(0);
    setAvgPace(0);
    setSplits([]);
    setElevationGain(0);
    setElevationLoss(0);
    setAutoPauseEvents([]);
    setIsAutoPaused(false);
    
    // Reset HR data
    setHeartRateData([]);
    setCurrentHR(0);
    setAvgHR(0);
    setMaxHR(0);
    setCurrentZone(0);
    setHrConnected(false);
    
    // Reset phase tracking
    setCurrentPhaseIndex(0);
    setPhaseTime(0);

    // Reset stage tracking back to initial state
    setCurrentStage(hasWarmup ? 'warmup' : 'main');
    setWarmupComplete(false);
    setMainWorkoutComplete(false);
    setWaitingForRunStart(false);

    // Close modal
    setShowRestartConfirm(false);

    // User stays in timer (ready to start fresh)
  };

  const currentExercise = exercises[currentExerciseIndex];

  // Sync exercise time display with stored times
  useEffect(() => {
    if (workoutType !== 'cardio' && exerciseTimes.length > 0) {
      setExerciseTime(exerciseTimes[currentExerciseIndex] || 0);
    }
  }, [currentExerciseIndex, workoutType, exerciseTimes]);

  // 🆕 JAN 29, 2026: TASK 1.8 - Reset selected image when exercise changes
  // 🆕 FEB 2, 2026 (Bug #60): Also collapse guidance drawer on exercise change
  useEffect(() => {
    setSelectedImageIndex(0); // Reset to first image on exercise change
    setIsGuidanceExpanded(false); // Collapse guidance for new exercise
  }, [currentExerciseIndex]);

  // 🆕 FEB 1, 2026: Load saved advancement mode preference
  useEffect(() => {
    const loadAdvancementPreference = async () => {
      try {
        const { storage } = await import('../services/storage');
        const saved = await storage.getItem(`advancement_mode_${workoutType}`);
        if (saved === 'manual' || saved === 'auto') {
          setAdvancementMode(saved as 'auto' | 'manual');
        }
      } catch (error) {
        console.warn('Failed to load advancement preference', error);
      }
    };
    
    if (workoutType === 'mobility' || workoutType === 'yoga') {
      loadAdvancementPreference();
    }
  }, [workoutType]);

  // 🆕 FEB 1, 2026: Auto-advance for timed exercises in AUTO mode
  // Applies to: mobility, yoga, AND timed strength exercises (duration-based, no sets/reps)
  // Timed strength exercises: circuits with "40 seconds per exercise" have duration but sets <= 1
  useEffect(() => {
    // Detect timed exercise: has duration, no meaningful rep-based sets
    // Timed exercise: has duration, no meaningful rep/set structure.
    // Guard against NaN reps (from parseInt on strings like "to failure").
    const _reps = currentExercise?.reps;
    const hasNoReps = !_reps || _reps === 0 || (typeof _reps === 'number' && isNaN(_reps));
    const isTimedExercise = currentExercise?.duration &&
      (!currentExercise?.sets || currentExercise.sets <= 1) && hasNoReps;
    const isTimedWorkoutType = workoutType === 'mobility' || workoutType === 'yoga' || isTimedExercise;

    if (isTimedWorkoutType &&
        advancementMode === 'auto' &&
        currentExercise?.duration &&
        exerciseTime >= currentExercise.duration &&
        !isResting &&
        currentExerciseIndex < exercises.length - 1 &&
        !showAutoAdvanceNotice) {

      // Voice announce transition during auto-advance
      if (voiceEnabled && voiceEngineRef.current) {
        const nextEx = exercises[currentExerciseIndex + 1];
        if (nextEx) {
          const exerciseName = nextEx.name || 'Next exercise';
          const dur = nextEx.duration ? `, ${nextEx.duration} seconds` : '';
          const cue = nextEx.form_cues?.[0] ? `. ${nextEx.form_cues[0]}` : '';
          // Show round info for circuit exercises
          const roundInfo = nextEx.circuit_info
            ? ` Round ${nextEx.circuit_info.round_number} of ${nextEx.circuit_info.total_rounds}.`
            : '';
          voiceEngineRef.current.speak(`Moving on.${roundInfo} ${exerciseName}. ${currentExerciseIndex + 2} of ${exercises.length}${dur}${cue}`, 'high');
        }
      }

      // Show "Moving to next..." notice
      setShowAutoAdvanceNotice(true);

      // Advance after 2s
      const timeout = setTimeout(() => {
        setShowAutoAdvanceNotice(false);
        nextExercise();
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [
    exerciseTime,
    currentExercise,
    workoutType,
    advancementMode,
    isResting,
    currentExerciseIndex,
    showAutoAdvanceNotice,
    exercises.length
  ]);

  // 🆕 FEB 14, 2026: Voice countdown warnings + periodic form cue reminders for timed exercises
  useEffect(() => {
    // Apply to mobility, yoga, AND timed strength exercises (duration-based, no sets/reps)
    // Timed exercise: has duration, no meaningful rep/set structure.
    // Guard against NaN reps (from parseInt on strings like "to failure").
    const _reps = currentExercise?.reps;
    const hasNoReps = !_reps || _reps === 0 || (typeof _reps === 'number' && isNaN(_reps));
    const isTimedExercise = currentExercise?.duration &&
      (!currentExercise?.sets || currentExercise.sets <= 1) && hasNoReps;
    const isTimedWorkoutType = workoutType === 'mobility' || workoutType === 'yoga' || isTimedExercise;
    if (!isTimedWorkoutType || !isRunning || isPaused || !voiceEnabled || !currentExercise?.duration) return;

    const secondsLeft = Math.floor(currentExercise.duration - exerciseTime);
    const elapsed = Math.floor(exerciseTime);

    // Countdown warnings (only when actively counting down, not at start)
    if (secondsLeft === 10 && elapsed > 0) {
      voiceEngineRef.current?.speak('10 seconds remaining.', 'normal');
    } else if (secondsLeft === 5 && elapsed > 0) {
      voiceEngineRef.current?.speak('5 seconds.', 'normal');
    }

    // Last pose completion announcement
    if (secondsLeft <= 0 && elapsed > 0 && currentExerciseIndex === exercises.length - 1) {
      // Only fire once — check if we just crossed 0
      if (secondsLeft === 0) {
        voiceEngineRef.current?.speak('Last pose complete. Great work!', 'high');
      }
    }

    // Periodic form cue reminders (~every 25 seconds, starting after 8 seconds, not in final 12 seconds)
    const cues = currentExercise.form_cues || [];
    if (cues.length > 0 && elapsed >= 8 && elapsed % 25 === 0 && secondsLeft > 12) {
      const cueIndex = Math.floor((elapsed - 8) / 25) % cues.length;
      voiceEngineRef.current?.speak(cues[cueIndex], 'low');
    }
  }, [exerciseTime, currentExercise, workoutType, isRunning, isPaused, voiceEnabled, currentExerciseIndex, exercises.length]);

  return (
    <div 
      className="fixed inset-0 flex flex-col"
      style={{
        position: 'fixed', // 🐛 BUG #82 FIX (Issue 4): Explicit fixed position to ensure overlay
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, var(--timer-bg-primary) 0%, var(--timer-bg-secondary) 100%)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: '0px',
        zIndex: 9999 // 🐛 BUG #77 FIX: Higher z-index to cover Start Session button
      }}
    >
      {/* 🆕 FEB 4, 2026 (Multi-Stage): Stage Progression Indicator */}
      {(hasWarmup || hasCooldown) && (
        <div 
          className="px-4 py-2 flex items-center justify-center gap-2 text-xs flex-shrink-0"
          style={{
            background: 'var(--timer-bg-primary)',
            borderBottom: '1px solid var(--timer-border-subtle)',
            color: 'var(--timer-text-tertiary)'
          }}
        >
          {hasWarmup && (
            <>
              <span style={{ color: currentStage === 'warmup' ? 'var(--timer-accent-secondary)' : 'inherit' }}>
                🔥 Warmup
              </span>
              <span>→</span>
            </>
          )}
          <span style={{ color: currentStage === 'main' ? 'var(--timer-accent-secondary)' : 'inherit' }}>
            🏃 Workout
          </span>
          {hasCooldown && (
            <>
              <span>→</span>
              <span style={{ color: currentStage === 'cooldown' ? 'var(--timer-accent-secondary)' : 'inherit' }}>
                ❄️ Cooldown
              </span>
            </>
          )}
        </div>
      )}
      
      {/* Header - Compact */}
      <div
        className="px-4 py-3 flex items-center justify-between flex-shrink-0"
        style={{
          background: 'var(--timer-bg-secondary)',
          borderBottom: '1px solid var(--timer-border-subtle)'
        }}
      >
        <div className="flex items-center gap-2">
          {/* Back arrow — navigates back to workout detail (with discard confirmation if session started) */}
          <button
            onClick={() => (isRunning || totalTime > 0) ? discardWorkout() : onExit()}
            className="p-2 -ml-2 rounded-full transition-all"
            style={{
              color: 'var(--timer-text-secondary)',
            }}
            aria-label="Go back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1
              className="text-base font-semibold"
              style={{ color: 'var(--timer-text-primary)' }}
            >
              {currentStage === 'warmup' && warmupMobility ? warmupMobility.title :
               currentStage === 'cooldown' && cooldownMobility ? cooldownMobility.title :
               workoutTitle}
            </h1>
            <p
              className="text-xs capitalize"
              style={{ color: 'var(--timer-text-tertiary)' }}
            >
              {currentStage === 'warmup' ? 'Warmup' :
               currentStage === 'cooldown' ? 'Cooldown' :
               `${workoutType} Session`}
            </p>
          </div>
        </div>
        {/* X close button — discards session with confirmation */}
        <button
          onClick={() => (isRunning || totalTime > 0) ? discardWorkout() : onExit()}
          className="p-2 rounded-full transition-all"
          style={{
            background: 'var(--timer-bg-tertiary)',
            color: 'var(--timer-text-secondary)',
          }}
          aria-label="Discard session"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* 🆕 FEB 4, 2026 (Bug #76): Brand-Aligned Phase Display with Exercise-Based Progress */}
      {workoutType === 'strength' && phases && phases.length > 0 && currentPhase && (
        <div 
          className="px-4 py-2 flex-shrink-0"
          style={{
            background: 'var(--timer-bg-secondary)',
            borderBottom: '1px solid var(--timer-border-medium)'
          }}
        >
          {/* Active Phase Progress Bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span 
                className="font-semibold"
                style={{ color: 'var(--timer-accent-secondary)' }}
              >
                {formatPhaseName(currentPhase)}
              </span>
              <span style={{ color: 'var(--timer-text-cedar)' }}>
                {formatTime(phaseTime)} / {formatTime(currentPhase.duration_seconds || 0)}
              </span>
            </div>
            <div 
              className="h-2 rounded-full overflow-hidden"
              style={{ background: 'var(--timer-phase-bar-bg)' }}
            >
              <div 
                className="h-full transition-all duration-500"
                style={{ 
                  background: 'var(--timer-phase-bar-fill)',
                  width: `${Math.min(100, getExerciseBasedPhaseProgress(
                    phaseTime,
                    currentExerciseIndex,
                    exercises.length,
                    currentPhase.duration_seconds || 0
                  ))}%`
                }}
              />
            </div>
          </div>
          
          {/* Upcoming Phases (Compact, Multi-line) */}
          {currentPhaseIndex < phases.length - 1 && (
            <div className="space-y-0.5">
              {phases.slice(currentPhaseIndex + 1).map((phase, idx) => {
                const phaseName = formatPhaseName(phase);
                const phaseTime = formatPhaseTime(phase.duration_seconds);
                
                return (
                  <div 
                    key={idx}
                    className="flex items-center justify-between text-xs"
                    style={{ color: 'var(--timer-text-tertiary)' }}
                  >
                    <span>{phaseName}</span>
                    <span>{phaseTime}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 🆕 HIIT Specialized Timer UIs */}
      {isEMOMMode && currentPhase && (
        <EMOMTimerDisplay
          totalMinutes={Math.floor((currentPhase.duration_seconds || 1200) / 60)}
          exercises={currentPhase.exercises_json || []}
          elapsedTime={phaseTime}
        />
      )}

      {isAMRAPMode && currentPhase && (
        <AMRAPTimerDisplay
          totalDuration={currentPhase.duration_seconds || 600}
          exercises={currentPhase.exercises_json || []}
          elapsedTime={phaseTime}
        />
      )}

      {/* 🆕 FEB 4, 2026 (Bug #76): Scrollable Content Area (flex-1 with min-h-0 critical!) */}
      {!isEMOMMode && !isAMRAPMode && (
        <div 
          ref={contentRef} 
          className="flex-1 flex flex-col items-center justify-start p-4 overflow-y-auto min-h-0"
          style={{
            background: 'transparent' // Inherits gradient from parent
          }}
        >
          {/* 🆕 FEB 4, 2026 (Multi-Stage): STAGE-AWARE CONTENT RENDERING */}
          
          {/* ===== STAGE 1: WARMUP ===== */}
          {currentStage === 'warmup' && warmupMobility && (
            <div className="w-full max-w-md">
              {/* Simple Timer Display */}
              <div
                className="text-center mb-6"
                style={{ color: 'var(--timer-text-primary)' }}
              >
                <div className="text-5xl font-mono font-bold mb-2">
                  {formatTime(totalTime)}
                </div>
                <div
                  className="text-sm"
                  style={{ color: 'var(--timer-text-tertiary)' }}
                >
                  Target: {warmupMobility.duration || (warmupMobility.duration_minutes ? `${warmupMobility.duration_minutes} min` : '8-10 min')}
                </div>
              </div>

              {/* Warmup Poses List — instructions + expandable form tips */}
              {(() => {
                const warmupExercises = warmupMobility.poses || warmupMobility.exercises_or_poses || warmupMobility.exercises || [];
                return warmupExercises.length > 0 ? (
                  <div className="space-y-3">
                    {warmupExercises.map((exercise: any, idx: number) => (
                      <WebTimerExerciseCard key={exercise.id || idx} exercise={exercise} index={idx} />
                    ))}
                  </div>
                ) : (
                  /* 🔧 MAR 17, 2026: Fallback when warmup exists but has no exercises */
                  <div
                    className="rounded-lg p-6 text-center"
                    style={{
                      background: 'var(--timer-card-bg)',
                      border: '1px solid var(--timer-card-border)'
                    }}
                  >
                    <p
                      className="text-sm"
                      style={{ color: 'var(--timer-text-tertiary)' }}
                    >
                      Follow your warmup routine, then tap below to start your workout.
                    </p>
                  </div>
                );
              })()}
              
              {/* 🔧 FEB 13: Always show skip/advance button once warmup timer is running */}
              {isRunning && (
                <button
                  onClick={advanceToMainWorkout}
                  className="w-full mt-6 px-6 py-4 rounded-xl font-bold text-lg transition-all"
                  style={{
                    background: 'var(--timer-accent-success)',
                    color: 'var(--colorsurface-50)',
                    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.4)'
                  }}
                >
                  ✓ Warmup Complete → Start Run
                </button>
              )}
            </div>
          )}
          
          {/* ===== STAGE 2: MAIN WORKOUT ===== */}
          {currentStage === 'main' && (
            <>
          
          {/* 🗑️ FEB 4, 2026 (Bug #76): Total Time moved to fixed bottom section (always visible) */}

          {/* 🆕 FEB 4, 2026 (Bug #76): Brand-Aligned Cardio Phase Display */}
          {workoutType !== 'strength' && phases.length > 0 && currentPhase && (
            <div 
              className="px-4 py-2 flex-shrink-0"
              style={{
                background: 'var(--timer-bg-secondary)',
                borderBottom: '1px solid var(--timer-border-medium)'
              }}
            >
              {/* Active Phase Progress Bar */}
              <div className="mb-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span 
                    className="font-semibold"
                    style={{ color: 'var(--timer-accent-secondary)' }}
                  >
                    {currentPhase.phase_name || currentPhase.name || 
                     (currentPhase.phase_type === 'warmup' ? 'Warmup' :
                      currentPhase.phase_type === 'cooldown' ? 'Cooldown' :
                      currentPhase.phase_type === 'recovery' ? 'Recovery' :
                      currentPhase.phase_type === 'interval' ? 'Intervals' :
                      currentPhase.phase_type === 'tempo' ? 'Tempo' :
                      currentPhase.phase_type === 'steady_state' ? 'Steady' :
                      `Phase ${currentPhaseIndex + 1}`)}
                  </span>
                  <span style={{ color: 'var(--timer-text-cedar)' }}>
                    {formatTime(phaseTime)} / {formatTime(currentPhase.duration_seconds || 0)}
                  </span>
                </div>
                <div 
                  className="h-2 rounded-full overflow-hidden"
                  style={{ background: 'var(--timer-phase-bar-bg)' }}
                >
                  <div 
                    className="h-full transition-all duration-1000"
                    style={{ 
                      background: 'var(--timer-phase-bar-fill)',
                      width: `${getPhaseProgress(currentPhase, phaseTime)}%`
                    }}
                  />
                </div>
              </div>
              
              {/* Pace/HR Targets (Compact Pills) */}
              {(currentPhase.target_pace_min_per_km || currentPhase.target_hr_min) && (
                <div className="flex gap-2 mb-2">
                  {currentPhase.target_pace_min_per_km && currentPhase.target_pace_max_per_km && (
                    <span 
                      className="text-xs px-2 py-0.5 rounded font-medium"
                      style={{
                        background: 'rgba(66, 165, 165, 0.2)',
                        color: 'var(--timer-accent-secondary)'
                      }}
                    >
                      {currentPhase.target_pace_min_per_km}-{currentPhase.target_pace_max_per_km}/km
                    </span>
                  )}
                  {currentPhase.target_hr_min && currentPhase.target_hr_max && (
                    <span 
                      className="text-xs px-2 py-0.5 rounded font-medium"
                      style={{
                        background: 'rgba(220, 38, 38, 0.2)',
                        color: '#FCA5A5'
                      }}
                    >
                      {currentPhase.target_hr_min}-{currentPhase.target_hr_max} bpm
                    </span>
                  )}
                </div>
              )}
              
              {/* 🆕 FEB 4, 2026: Collapsible Phase Coaching */}
              {(currentPhase.instructions || currentPhase.notes) && (
                <div className="mb-2">
                  <button
                    onClick={() => setIsPhaseGuidanceExpanded(!isPhaseGuidanceExpanded)}
                    className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-all"
                    style={{
                      background: isPhaseGuidanceExpanded 
                        ? 'rgba(31, 108, 79, 0.3)'
                        : 'rgba(31, 108, 79, 0.2)',
                      border: '1px solid var(--timer-border-subtle)',
                      color: 'var(--timer-text-cedar)'
                    }}
                  >
                    <span className="text-xs font-medium">
                      💬 Phase Coaching
                    </span>
                    <svg
                      className={`w-3 h-3 transition-transform ${isPhaseGuidanceExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ color: 'var(--timer-accent-secondary)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isPhaseGuidanceExpanded && (
                    <div 
                      className="mt-2 px-3 py-2 rounded text-xs leading-relaxed"
                      style={{
                        background: 'rgba(17, 60, 43, 0.4)',
                        border: '1px solid var(--timer-border-subtle)',
                        color: 'var(--timer-text-secondary)'
                      }}
                    >
                      {currentPhase.instructions || currentPhase.notes}
                    </div>
                  )}
                </div>
              )}
              
              {/* Upcoming Phases (Compact List) */}
              {currentPhaseIndex < phases.length - 1 && (
                <div className="space-y-0.5">
                  {phases.slice(currentPhaseIndex + 1).map((phase, idx) => {
                    const phaseName = formatPhaseName(phase);
                    const phaseTime = formatPhaseTime(phase.duration_seconds);
                    
                    return (
                      <div 
                        key={idx}
                        className="flex items-center justify-between text-xs"
                        style={{ color: 'var(--timer-text-tertiary)' }}
                      >
                        <span>{phaseName}</span>
                        <span>{phaseTime}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 🗑️ FEB 4, 2026: REMOVED old verbose cardio phase card - replaced with simplified header above */}

          {/* Cardio-specific displays (GPS, distance, pace) */}
          {workoutType === 'cardio' && (
          <>
            {/* 🆕 Auto-Pause Indicator */}
            {isAutoPaused && (
              <div className="mb-4 px-4 py-2 bg-yellow-900/30 border border-yellow-600 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-yellow-400">
                  <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium">Auto-Paused (Speed below threshold)</span>
                </div>
              </div>
            )}

            {/* 🐛 BUG #82 FIX: GPS metrics text-white for dark timer background */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-md mb-3">
              <div className="text-center">
                <div className="text-xl font-bold text-white">{(distance / 1000).toFixed(2)}</div>
                <div className="text-gray-400 text-xs">Distance (km)</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-white">{formatPace(currentPace)}</div>
                <div className="text-gray-400 text-xs">Current Pace</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-white">{formatPace(avgPace)}</div>
                <div className="text-gray-400 text-xs">Avg Pace</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-white">
                  {gpsAccuracy ? `${Math.round(gpsAccuracy)}m` : '--'}
                </div>
                <div className="text-gray-400 text-xs">GPS Accuracy</div>
              </div>
            </div>

            {/* 🆕 Heart Rate Display */}
            {hrConnected && currentHR > 0 && (
              <div className="w-full max-w-md mb-4 bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold">{currentHR}</div>
                    <div className="text-xs text-gray-400">BPM</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-lg font-semibold ${
                      currentZone === 1 ? 'text-gray-400' :
                      currentZone === 2 ? 'text-blue-400' :
                      currentZone === 3 ? 'text-green-400' :
                      currentZone === 4 ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      Zone {currentZone}
                    </div>
                    <div className="text-xs text-gray-400">
                      {hrTrackerRef.current?.getZoneInfo(currentZone).name}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg">{Math.round(avgHR)}</div>
                    <div className="text-xs text-gray-400">Avg</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg">{maxHR}</div>
                    <div className="text-xs text-gray-400">Max</div>
                  </div>
                </div>
              </div>
            )}

            {/* 🆕 Elevation Display */}
            {(elevationGain > 0 || elevationLoss > 0) && (
              <div className="flex gap-4 justify-center mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-green-400">↑</span>
                  <span className="text-gray-300">{Math.round(elevationGain)}m gain</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-red-400">↓</span>
                  <span className="text-gray-300">{Math.round(elevationLoss)}m loss</span>
                </div>
              </div>
            )}

            {/* 🆕 Splits Display (Expandable) */}
            {splits.length > 0 && (
              <details className="w-full max-w-md mb-4">
                <summary className="cursor-pointer text-center text-sm text-gray-400 hover:text-gray-300 py-2">
                  {splits.length} Split{splits.length > 1 ? 's' : ''} Recorded ▼
                </summary>
                <div className="mt-2 bg-gray-800 rounded-lg p-3 space-y-1">
                  {splits.map((split, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">
                        {split.type === 'manual' ? '📍' : ''}
                        {split.type === 'manual' ? `Lap ${idx + 1}` : `${split.km}km`}
                      </span>
                      <span className="text-gray-300">{formatTime(split.time)}</span>
                      <span className="text-blue-400 font-mono">{formatPace(split.pace)}/km</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* 🆕 Manual Lap Button */}
            {isRunning && environment === 'outdoor' && (
              <button
                onClick={handleManualLap}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors mb-4"
              >
                📍 Mark Lap
              </button>
            )}

            {/* 🔧 FEB 13: MiniRouteMap REMOVED from live timer.
                Route visualization belongs in the post-workout review (WorkoutDetail),
                not the live timer — nobody looks at a map mid-run. */}
          </>
        )}

        {/* 🆕 FEB 1, 2026: REDESIGNED Exercise-specific displays (Strength/Mobility/Yoga) */}
        {workoutType !== 'cardio' && exercises.length > 0 && (
          <div className="w-full max-w-md mb-4">
            {/* Circuit Information - Show if circuit training (keep this) */}
            {currentExercise?.circuit_info && (
              <div className="mb-6 bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      <span className="text-purple-400 font-semibold text-sm">
                        Circuit {currentExercise.circuit_info.circuit_number}
                      </span>
                    </div>
                    {currentExercise.circuit_info.round_number && currentExercise.circuit_info.total_rounds && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-blue-400 text-sm">
                          Round {currentExercise.circuit_info.round_number} of {currentExercise.circuit_info.total_rounds}
                        </span>
                      </div>
                    )}
                  </div>
                  {currentExercise.circuit_info.is_final_round && (
                    <span className="text-yellow-400 text-xs font-medium px-2 py-1 bg-yellow-400/10 rounded">
                      FINAL ROUND
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-gray-400 text-sm">Exercise:</span>
                  <div className="flex gap-1">
                    {Array.from({ length: currentExercise.circuit_info.total_exercises_in_circuit }, (_, i) => (
                      <div
                        key={i}
                        className={`w-6 h-2 rounded-full ${
                          i + 1 === currentExercise.circuit_info!.exercise_in_circuit ? 'bg-green-500' :
                          i + 1 < currentExercise.circuit_info!.exercise_in_circuit ? 'bg-gray-500' : 'bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 🆕 FEB 1, 2026: Auto/Manual Toggle (timed exercises: Mobility/Yoga/Timed Circuits) */}
            {(() => { const _r = currentExercise?.reps; const _noReps = !_r || _r === 0 || (typeof _r === 'number' && isNaN(_r)); return (workoutType === 'mobility' || workoutType === 'yoga' || (currentExercise?.duration && (!currentExercise?.sets || currentExercise.sets <= 1) && _noReps)); })() && (
              <div className="flex items-center justify-end mb-3 px-1">
                <div className="flex items-center gap-1 bg-gray-800 rounded-full p-1">
                  <button
                    onClick={() => handleAdvancementModeChange('auto')}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      advancementMode === 'auto'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    AUTO
                  </button>
                  <button
                    onClick={() => handleAdvancementModeChange('manual')}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      advancementMode === 'manual'
                        ? 'bg-gray-600 text-white shadow-md'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    MANUAL
                  </button>
                </div>
              </div>
            )}

            {/* 🆕 Exercise Queue Strip — dot progress indicator with tap navigation */}
            {exercises.length > 1 && (
              <div className="w-full mb-3 px-2">
                <div 
                  className="flex items-center justify-center gap-1.5 py-2 overflow-x-auto scrollbar-hide"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {exercises.map((_: any, i: number) => {
                    const isCurrent = i === currentExerciseIndex;
                    const isCompleted = i < currentExerciseIndex;
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          if (i !== currentExerciseIndex) {
                            setCurrentExerciseIndex(i);
                            setCurrentFormTipIndex(0);
                            setExerciseTime(exerciseTimes[i] || 0);
                            if (i < currentExerciseIndex) {
                              // Going back — keep current set
                            } else {
                              setCurrentSet(1);
                            }
                            setIsResting(false);
                            setRestTimeRemaining(0);
                          }
                        }}
                        className="flex-shrink-0 rounded-full transition-all duration-200 focus:outline-none"
                        style={{
                          width: isCurrent ? 28 : 10,
                          height: 10,
                          background: isCurrent
                            ? 'var(--timer-accent-secondary)'
                            : isCompleted
                              ? 'var(--timer-accent-success)'
                              : 'rgba(255,255,255,0.2)',
                          border: isCurrent
                            ? '2px solid var(--timer-accent-secondary)'
                            : 'none',
                          cursor: 'pointer',
                        }}
                        aria-label={`Exercise ${i + 1} of ${exercises.length}: ${exercises[i]?.name || ''}`}
                        title={exercises[i]?.name || `Exercise ${i + 1}`}
                      />
                    );
                  })}
                </div>
                {/* Current position label */}
                <div 
                  className="text-center text-xs mt-0.5"
                  style={{ color: 'var(--timer-text-tertiary)' }}
                >
                  {currentExerciseIndex + 1} of {exercises.length}
                </div>
              </div>
            )}

            {/* 🆕 FEB 4, 2026 (Bug #76): Cedar Exercise Card with Brand Colors */}
            {currentExercise && (
              <div 
                className="w-full mb-4 rounded-xl p-5"
                style={{
                  background: 'var(--timer-card-bg)',
                  border: '2px solid var(--timer-card-border)',
                  boxShadow: '0 4px 12px rgba(31, 108, 79, 0.2)'
                }}
              >
                <div className="text-center">
                  {/* Exercise name at top of cedar card */}
                  <div 
                    className="text-2xl font-bold mb-4"
                    style={{ color: 'var(--timer-text-primary)' }}
                  >
                    {currentExercise.name}
                  </div>
                  
                  {/* 🐛 BUG #75 FIX: ALWAYS show image area (with placeholder if no image) */}
                  <div className="mb-4">
                    <div 
                      className="relative rounded-lg overflow-hidden"
                      style={{
                        background: 'var(--timer-bg-tertiary)',
                        border: '1px solid var(--timer-border-subtle)'
                      }}
                    >
                      <img 
                        src={currentExercise.images && currentExercise.images.length > 0 
                          ? (currentExercise.images[selectedImageIndex] || currentExercise.images[0])
                          : '/img/image-coming-soon.svg'
                        }
                        alt={currentExercise.name}
                        className={`w-full h-48 ${
                          (!currentExercise.images || currentExercise.images.length === 0 || 
                           currentExercise.images[selectedImageIndex]?.includes('image-coming-soon'))
                            ? 'object-contain' 
                            : 'object-cover'
                        }`}
                        onError={(e) => {
                          e.currentTarget.src = '/img/image-coming-soon.svg';
                          e.currentTarget.className = 'w-full h-48 object-contain';
                        }}
                      />
                      
                      {/* Image counter (if multiple) */}
                      {currentExercise.images && currentExercise.images.length > 1 && (
                        <div 
                          className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: 'rgba(17, 60, 43, 0.9)',
                            color: 'var(--timer-text-cedar)'
                          }}
                        >
                          {selectedImageIndex + 1} / {currentExercise.images.length}
                        </div>
                      )}
                    </div>
                    
                    {/* Thumbnail gallery (if multiple images) */}
                    {currentExercise.images && currentExercise.images.length > 1 && (
                      <div className="flex gap-2 justify-center mt-2 overflow-x-auto pb-1">
                        {currentExercise.images.map((imgUrl, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedImageIndex(idx)}
                              className="flex-shrink-0 w-12 h-12 rounded"
                              style={{
                                border: idx === selectedImageIndex
                                  ? '2px solid var(--timer-accent-secondary)'
                                  : '1px solid var(--timer-border-medium)',
                                boxShadow: idx === selectedImageIndex 
                                  ? '0 0 0 1px var(--timer-accent-secondary)'
                                  : 'none'
                              }}
                          >
                            <img 
                              src={imgUrl}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* 🆕 FEB 14: Set Progress Chips + Inline Logging */}
                  {workoutType === 'strength' && currentExercise.sets && currentExercise.sets > 0 ? (
                    <div className="w-full mb-4">
                      {/* Set Progress Chips */}
                      <div className="flex items-center justify-center gap-1.5 mb-3">
                        {Array.from({ length: currentExercise.sets }, (_, i) => {
                          const isCompleted = i < setsCompletedForExercise;
                          const isActive = i === setsCompletedForExercise && !isResting;
                          return (
                            <div
                              key={i}
                              className="flex items-center justify-center rounded-full text-xs font-bold transition-all duration-300"
                              style={{
                                width: isActive ? 36 : 28,
                                height: isActive ? 36 : 28,
                                background: isCompleted
                                  ? 'var(--timer-text-cedar)'
                                  : isActive
                                    ? 'rgba(46, 94, 52, 0.25)'
                                    : 'var(--timer-bg-tertiary)',
                                color: isCompleted
                                  ? '#fff'
                                  : isActive
                                    ? 'var(--timer-text-cedar)'
                                    : 'var(--timer-text-tertiary)',
                                border: isActive ? '2px solid var(--timer-text-cedar)' : '1px solid transparent',
                                boxShadow: isActive ? '0 0 8px rgba(46, 94, 52, 0.4)' : 'none',
                              }}
                            >
                              {isCompleted ? '✓' : i + 1}
                            </div>
                          );
                        })}
                      </div>

                      {/* Active Set Info — reps + rest */}
                      <div className="flex items-center justify-center gap-2 text-sm mb-3" style={{ color: 'var(--timer-text-secondary)' }}>
                        <span className="font-mono font-semibold" style={{ color: 'var(--timer-text-cedar)' }}>
                          {currentExercise.reps || '—'}
                        </span>
                        <span>reps</span>
                        {currentExercise.restPeriod && (
                          <>
                            <span style={{ color: 'var(--timer-text-tertiary)' }}>•</span>
                            <span>{currentExercise.restPeriod}s rest</span>
                          </>
                        )}
                      </div>

                      {/* Inline Weight / Rep Logging — only during active set */}
                      {!isResting && setsCompletedForExercise < currentExercise.sets && (
                        <div 
                          className="flex items-center justify-center gap-3 px-3 py-2 rounded-lg"
                          style={{ background: 'var(--timer-bg-tertiary)', border: '1px solid var(--timer-border-subtle)' }}
                        >
                          {requiresWeight(currentExercise) && (() => {
                            const storedKg = actualWeights[currentExercise.id]?.[setsCompletedForExercise];
                            const displayValue = typeof storedKg === 'number' ? String(kgToDisplay(storedKg, units)) : '';
                            return (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs" style={{ color: 'var(--timer-text-tertiary)' }}>{weightUnitLabel(units)}</span>
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  className="w-16 text-center rounded px-1 py-1 text-sm font-mono"
                                  style={{
                                    background: 'var(--timer-bg-secondary)',
                                    color: 'var(--timer-text-primary)',
                                    border: '1px solid var(--timer-border-subtle)',
                                  }}
                                  placeholder="—"
                                  value={displayValue}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    if (!isNaN(v)) logSetWeight(currentExercise.id, setsCompletedForExercise, displayToKg(v, units));
                                  }}
                                />
                              </div>
                            );
                          })()}
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs" style={{ color: 'var(--timer-text-tertiary)' }}>reps</span>
                            <input
                              type="number"
                              inputMode="numeric"
                              className="w-16 text-center rounded px-1 py-1 text-sm font-mono"
                              style={{
                                background: 'var(--timer-bg-secondary)',
                                color: 'var(--timer-text-primary)',
                                border: '1px solid var(--timer-border-subtle)',
                              }}
                              placeholder={currentExercise.reps != null ? String(currentExercise.reps) : '—'}
                              value={String(actualReps[currentExercise.id]?.[setsCompletedForExercise] ?? '')}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (!isNaN(v)) logSetReps(currentExercise.id, setsCompletedForExercise, v);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Non-strength: simple reps/duration display */
                    <div className="flex items-center justify-center gap-2 text-lg mb-4">
                      <span className="font-mono" style={{ color: 'var(--timer-text-cedar)' }}>
                        {currentExercise.reps || (currentExercise.duration ? `${currentExercise.duration}s` : '?')}
                      </span>
                    </div>
                  )}
                  
                  {/* 🆕 Rotating Form Tips — auto-cycles every 5s, tap to advance */}
                  {(() => {
                    const tips = (currentExercise.form_cues && Array.isArray(currentExercise.form_cues) && currentExercise.form_cues.length > 0)
                      ? currentExercise.form_cues
                      : (currentExercise.safety_notes && Array.isArray(currentExercise.safety_notes) && currentExercise.safety_notes.length > 0)
                        ? currentExercise.safety_notes
                        : [];
                    if (tips.length === 0) return null;
                    const tipIndex = currentFormTipIndex % tips.length;
                    return (
                      <div
                        className="mt-4 flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer select-none"
                        style={{
                          background: 'rgba(31, 108, 79, 0.2)',
                          border: '1px solid var(--timer-border-subtle)',
                        }}
                        onClick={() => setCurrentFormTipIndex((tipIndex + 1) % tips.length)}
                      >
                        <span
                          className="flex-shrink-0 text-base"
                          style={{ color: 'var(--timer-accent-secondary)' }}
                          aria-hidden="true"
                        >
                          💡
                        </span>
                        <span
                          className="text-sm leading-snug flex-1"
                          style={{ color: 'var(--timer-text-secondary)' }}
                        >
                          {tips[tipIndex]}
                        </span>
                        {tips.length > 1 && (
                          <div className="flex gap-1 flex-shrink-0">
                            {tips.map((_: string, i: number) => (
                              <span
                                key={i}
                                className="block rounded-full transition-all"
                                style={{
                                  width: i === tipIndex ? 12 : 5,
                                  height: 5,
                                  background: i === tipIndex
                                    ? 'var(--timer-accent-secondary)'
                                    : 'rgba(255,255,255,0.25)',
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Equipment/Weight Guidance */}
                  {(!currentExercise.images || 
                    currentExercise.images.every((img: string) => 
                      !img.includes('dumbbell') && !img.includes('barbell') && !img.includes('weight')
                    )) && (
                    <div className="inline-block px-3 py-1 bg-yellow-900/30 border border-yellow-600 rounded-full">
                      <span className="text-xs text-yellow-300 font-medium">No added weight - bodyweight only</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 🆕 FEB 1, 2026: Mode-aware timer display for timed exercises (Mobility/Yoga/Timed Circuits) */}
            {(() => { const _r = currentExercise?.reps; const _noReps = !_r || _r === 0 || (typeof _r === 'number' && isNaN(_r)); return (workoutType === 'mobility' || workoutType === 'yoga' || (currentExercise?.duration && (!currentExercise?.sets || currentExercise.sets <= 1) && _noReps)); })() && currentExercise?.duration && (
              <div className="text-center mb-4">
                {advancementMode === 'auto' ? (
                  <>
                    {/* Countdown timer (auto mode) */}
                    <div className="text-3xl font-mono font-bold text-white mb-1">
                      {Math.max(0, currentExercise.duration - exerciseTime)}s
                    </div>
                    <div className="text-sm text-gray-400">remaining</div>
                    
                    {/* Progress bar */}
                    <div className="w-full max-w-xs mx-auto mt-2 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all duration-1000"
                        style={{ width: `${Math.min(100, (exerciseTime / currentExercise.duration) * 100)}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Elapsed timer (manual mode) */}
                    <div className="text-3xl font-mono font-bold text-white mb-1">
                      {exerciseTime}s
                    </div>
                    <div className="text-sm text-gray-400">
                      suggested: {currentExercise.duration}s
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 🆕 FEB 14: Enhanced Rest Timer with What's Next + Skip */}
            {workoutType === 'strength' && isResting && (
              <div 
                className="w-full mb-4 rounded-lg p-4"
                style={{
                  background: 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.35)'
                }}
              >
                {/* Top Row: "Resting" label + Skip button */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium" style={{ color: '#FCD34D' }}>
                    {setsCompletedForExercise < (currentExercise?.sets || 0)
                      ? `Rest — Set ${setsCompletedForExercise} of ${currentExercise?.sets}`
                      : 'Rest before next exercise'}
                  </span>
                  <button
                    onClick={skipRest}
                    className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                    style={{
                      background: 'rgba(245, 158, 11, 0.25)',
                      color: '#FCD34D',
                      border: '1px solid rgba(245, 158, 11, 0.5)',
                    }}
                  >
                    Skip →
                  </button>
                </div>

                {/* Circle Timer + Adjust Buttons */}
                <div className="flex items-center justify-center gap-5">
                  <div className="relative inline-flex items-center justify-center flex-shrink-0">
                    <svg className="w-16 h-16 transform -rotate-90">
                      <circle cx="32" cy="32" r="28" stroke="var(--timer-phase-bar-bg)" strokeWidth="4" fill="none" />
                      <circle
                        cx="32" cy="32" r="28"
                        stroke={restTimeRemaining <= 5 ? 'var(--timer-accent-danger)' : 'var(--timer-accent-secondary)'}
                        strokeWidth="4" fill="none"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - restTimeRemaining / (currentExercise?.restPeriod || 90))}`}
                        strokeLinecap="round" className="transition-all"
                      />
                    </svg>
                    <div className="absolute text-2xl font-bold" style={{ color: 'var(--timer-text-primary)' }}>
                      {restTimeRemaining}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => adjustRestTime(15)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={{ background: 'var(--timer-bg-tertiary)', color: 'var(--timer-text-cedar)', border: '1px solid var(--timer-border-subtle)' }}
                    >
                      +15s
                    </button>
                    <button
                      onClick={() => adjustRestTime(-15)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={{ background: 'var(--timer-bg-tertiary)', color: 'var(--timer-text-cedar)', border: '1px solid var(--timer-border-subtle)' }}
                    >
                      −15s
                    </button>
                  </div>
                </div>

                {/* Form Tips — collapsible bullet list during rest */}
                {(() => {
                  const tips = currentExercise?.form_cues || currentExercise?.safety_notes || [];
                  if (tips.length === 0) return null;
                  return (
                    <div
                      className="mt-3 pt-3"
                      style={{ borderTop: '1px solid rgba(245, 158, 11, 0.2)' }}
                    >
                      <button
                        onClick={() => setIsGuidanceExpanded(!isGuidanceExpanded)}
                        className="w-full flex items-center justify-between text-xs font-medium px-1 py-1"
                        style={{ color: 'var(--timer-accent-secondary)' }}
                      >
                        <span>Form Tips</span>
                        <svg
                          className={`w-3.5 h-3.5 transition-transform ${isGuidanceExpanded ? 'rotate-180' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isGuidanceExpanded && (
                        <ul className="mt-1.5 space-y-1 pl-1">
                          {tips.map((tip: string, i: number) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-xs leading-snug"
                              style={{ color: 'var(--timer-text-secondary)' }}
                            >
                              <span className="flex-shrink-0 mt-0.5" style={{ color: 'var(--timer-accent-secondary)' }}>&#8226;</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })()}

                {/* What's Next Preview */}
                {(() => {
                  const totalSets = currentExercise?.sets || 0;
                  const isInterExercise = setsCompletedForExercise >= totalSets;
                  const nextEx = isInterExercise ? exercises[currentExerciseIndex + 1] : null;

                  return (
                    <div
                      className="mt-3 pt-3 text-xs"
                      style={{ borderTop: '1px solid rgba(245, 158, 11, 0.2)', color: 'var(--timer-text-tertiary)' }}
                    >
                      {isInterExercise && nextEx ? (
                        <div className="flex items-center gap-3">
                          {nextEx.images && nextEx.images[0] && (
                            <img
                              src={nextEx.images[0]}
                              alt={nextEx.name}
                              className="w-10 h-10 rounded object-cover flex-shrink-0"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          )}
                          <span className="text-center flex-1">
                            Up next: <strong style={{ color: 'var(--timer-text-secondary)' }}>{nextEx.name}</strong>
                            {nextEx.sets ? ` — ${nextEx.sets} \u00d7 ${nextEx.reps || '?'}` : ''}
                          </span>
                        </div>
                      ) : !isInterExercise ? (
                        <div className="text-center">
                          Next: <strong style={{ color: 'var(--timer-text-secondary)' }}>Set {setsCompletedForExercise + 1}</strong> of {totalSets}
                          {currentExercise.reps ? ` — ${currentExercise.reps} reps` : ''}
                        </div>
                      ) : (
                        <div className="text-center" style={{ color: '#FCD34D' }}>Last exercise — finish strong!</div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* 🆕 FEB 1, 2026: Auto-advance notice (Mobility/Yoga/Timed Circuits AUTO mode) */}
            {advancementMode === 'auto' && showAutoAdvanceNotice && (
              <div className="bg-green-900/30 border border-green-600 rounded-lg px-4 py-3 mb-3 animate-pulse">
                <div className="text-green-400 font-semibold text-center">
                  ✅ {workoutType === 'mobility' || workoutType === 'yoga' ? 'Hold Complete! Moving to next pose...' : 'Done! Moving to next exercise...'}
                </div>
              </div>
            )}

            {/* 🆕 FEB 4, 2026 (Bug #76): Complete Set + Skip (70/30 Layout) */}
            {workoutType === 'strength' && !isResting && currentExercise?.sets && (
              <div className="w-full px-2 mb-4">
                <div className="flex gap-2 items-center">
                  {/* Complete Set Button (70% width - primary action) */}
                  <button
                    onClick={completeSet}
                    disabled={!isRunning}
                    className="px-6 py-4 rounded-xl font-bold text-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{
                      flex: '7',
                      background: 'var(--timer-accent-success)',
                      color: 'var(--colorsurface-50)',
                      boxShadow: isRunning ? '0 4px 12px rgba(46, 125, 50, 0.4)' : 'none'
                    }}
                  >
                    ✓ Complete set {currentSet}/{currentExercise.sets}
                  </button>
                  
                  {/* Skip Button (30% width - secondary action) */}
                  <button
                    onClick={skipToNextExercise}
                    disabled={!isRunning || currentExerciseIndex === exercises.length - 1}
                    className="px-4 py-4 rounded-lg font-medium text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{
                      flex: '3',
                      background: 'var(--timer-bg-tertiary)',
                      color: 'var(--timer-text-cedar)',
                      border: '1px solid var(--timer-border-medium)'
                    }}
                  >
                    ⏭️ Skip
                  </button>
                </div>
                <div 
                  className="text-center text-xs mt-2"
                  style={{ color: 'var(--timer-text-tertiary)' }}
                >
                  {isRunning 
                    ? 'Complete set when done, or Skip to move ahead' 
                    : 'Press GO! to begin'
                  }
                </div>
              </div>
            )}

            {/* 🆕 FEB 1, 2026: Hybrid Navigation Button (Mobility/Yoga) */}
            {(workoutType === 'mobility' || workoutType === 'yoga') && !isResting && (
              <div className="w-full px-2 mb-4">
                <button
                  onClick={nextExercise}
                  disabled={currentExerciseIndex === exercises.length - 1}
                  className={`w-full px-6 py-4 rounded-xl font-bold text-lg text-white transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    advancementMode === 'auto'
                      ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                      : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                  }`}
                >
                  {advancementMode === 'auto' ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                      </svg>
                      Skip to Next Pose
                    </>
                  ) : (
                    <>
                      Next Pose
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
                <div className="text-center text-xs text-gray-400 mt-2">
                  {advancementMode === 'auto' 
                    ? 'Auto-advancing when timer reaches 0:00'
                    : currentExercise?.duration 
                      ? `Suggested hold: ${currentExercise.duration}s` 
                      : 'Move to next pose when ready'}
                </div>
              </div>
            )}

            {/* 🔧 FEB 3, 2026: Removed standalone Previous/Next (now flanking Complete Set button) */}
          </div>
        )}

        {/* GPS Status and Voice Toggle for cardio */}
        {workoutType === 'cardio' && (
          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-center gap-2 text-sm">
              <div className={`w-3 h-3 rounded-full ${
                gpsPermission === 'granted' && gpsAccuracy && gpsAccuracy < 20 
                  ? 'bg-green-500' 
                  : gpsPermission === 'granted' 
                  ? 'bg-yellow-500' 
                  : 'bg-red-500'
              }`}></div>
              <span className="text-gray-300">
                {gpsPermission === 'granted' 
                  ? gpsAccuracy && gpsAccuracy < 20 
                    ? 'GPS: Good Signal' 
                    : 'GPS: Weak Signal'
                  : 'GPS: No Permission'
                }
              </span>
            </div>
            
            {/* 🆕 Voice Guidance Toggle */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  voiceEnabled 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {voiceEnabled ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  )}
                </svg>
                <span>{voiceEnabled ? 'Voice On' : 'Voice Off'}</span>
              </button>
              
              {/* 🆕 Heart Rate Connection Button */}
              <button
                onClick={hrConnected ? disconnectHeartRate : connectHeartRate}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  hrConnected 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>{hrConnected ? 'Disconnect HR' : 'Connect HR'}</span>
              </button>
            </div>
          </div>
          )}

          </>
        )}
        
        {/* ===== STAGE 3: COOLDOWN ===== */}
        {currentStage === 'cooldown' && cooldownMobility && (
          <div className="w-full max-w-md">
            {/* Simple Timer Display */}
            <div 
              className="text-center mb-6"
              style={{ color: 'var(--timer-text-primary)' }}
            >
              <div className="text-5xl font-mono font-bold mb-2">
                {formatTime(totalTime)}
              </div>
              <div 
                className="text-sm"
                style={{ color: 'var(--timer-text-tertiary)' }}
              >
                Target: {cooldownMobility.duration || (cooldownMobility.duration_minutes ? `${cooldownMobility.duration_minutes} min` : '8-10 min')}
              </div>
            </div>

            {/* Cooldown Poses List — instructions + expandable form tips */}
            {(() => {
              const cooldownExercises = cooldownMobility.poses || cooldownMobility.exercises_or_poses || cooldownMobility.exercises || [];
              return cooldownExercises.length > 0 ? (
                <div className="space-y-3">
                  {cooldownExercises.map((exercise: any, idx: number) => (
                    <WebTimerExerciseCard key={exercise.id || idx} exercise={exercise} index={idx} />
                  ))}
                </div>
              ) : (
                /* 🔧 MAR 17, 2026: Fallback when cooldown exists but has no exercises */
                <div
                  className="rounded-lg p-6 text-center"
                  style={{
                    background: 'var(--timer-card-bg)',
                    border: '1px solid var(--timer-card-border)'
                  }}
                >
                  <p
                    className="text-sm"
                    style={{ color: 'var(--timer-text-tertiary)' }}
                  >
                    Follow your cooldown routine, then tap below to finish.
                  </p>
                </div>
              );
            })()}
            
            {/* Cooldown Actions (After timer started) */}
            {isRunning && (
              <div className="space-y-3 mt-6">
                {/* Complete Cooldown Button */}
                <button
                  onClick={completeCooldown}
                  className="w-full px-6 py-4 rounded-xl font-bold text-lg transition-all"
                  style={{
                    background: 'var(--timer-accent-success)',
                    color: 'var(--colorsurface-50)',
                    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.4)'
                  }}
                >
                  ✓ Complete Cooldown
                </button>
                
                {/* Skip Cooldown Button */}
                <button
                  onClick={() => setShowSkipCooldownModal(true)}
                  className="w-full px-6 py-3 rounded-lg font-medium text-base transition-all"
                  style={{
                    background: 'var(--timer-bg-tertiary)',
                    color: 'var(--timer-text-cedar)',
                    border: '1px solid var(--timer-border-medium)'
                  }}
                >
                  ⏭️ Skip Cooldown
                </button>
              </div>
            )}
          </div>
        )}
        
        </div>
      )}

      {/* 🆕 FEB 4, 2026 (Bug #76): FIXED BOTTOM SECTION - Total Time + Controls (Non-Scrollable) */}
      <div className="flex-shrink-0">
        {/* Total Time Bar */}
        <div 
          className="px-4 py-2 flex items-center justify-center gap-2 text-sm"
          style={{
            background: 'var(--timer-bg-secondary)',
            borderTop: '1px solid var(--timer-border-medium)',
            borderBottom: '1px solid var(--timer-border-medium)'
          }}
        >
          <span style={{ color: 'var(--timer-text-tertiary)' }}>Total:</span>
          <span 
            className="font-mono font-bold"
            style={{ color: 'var(--timer-accent-secondary)' }}
          >
            {formatTime(totalTime)}
          </span>
          {(workoutType === 'mobility' || workoutType === 'yoga') && (
            <>
              <span style={{ color: 'var(--timer-text-tertiary)' }}>|</span>
              <span style={{ color: 'var(--timer-text-tertiary)' }}>
                Pose {currentExerciseIndex + 1} of {exercises.length}
              </span>
            </>
          )}
        </div>
        
        {/* 🆕 FEB 4, 2026 (Multi-Stage): Stage-Aware Control Buttons */}
        <div 
          className="flex flex-wrap gap-3 justify-center items-center px-4 py-3"
          style={{
            background: 'var(--timer-bg-primary)',
            borderTop: '2px solid var(--timer-accent-primary)',
            paddingBottom: 'calc(68px + env(safe-area-inset-bottom, 0px))'
          }}
        >
          {/* WARMUP STAGE BUTTONS */}
          {currentStage === 'warmup' && (
            <>
              {!isRunning ? (
                <button
                  onClick={startWorkout}
                  className="px-8 py-3 rounded-full text-base font-bold transition-all"
                  style={{
                    background: 'var(--timer-button-primary)',
                    color: 'var(--colorsurface-50)',
                    boxShadow: '0 4px 12px rgba(31, 108, 79, 0.4)'
                  }}
                >
                  GO!
                </button>
              ) : (
                <>
                  <button
                    onClick={isPaused ? resumeWorkout : pauseWorkout}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: isPaused ? 'var(--timer-button-primary)' : 'var(--timer-accent-warning)',
                      color: 'var(--colorsurface-50)'
                    }}
                  >
                    {isPaused ? '▶ Resume' : '⏸ Pause'}
                  </button>
                  {/* 🔧 FEB 13: Always show skip/advance button once warmup timer is running */}
                  <button
                    onClick={advanceToMainWorkout}
                    className="px-6 py-3 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: 'var(--timer-accent-success)',
                      color: 'var(--colorsurface-50)'
                    }}
                  >
                    → Start Run
                  </button>
                </>
              )}
            </>
          )}
          
          {/* MAIN WORKOUT STAGE BUTTONS */}
          {currentStage === 'main' && (
            <>
              {!isRunning ? (
                <button
                  onClick={startWorkout}
                  className="px-8 py-3 rounded-full text-base font-bold transition-all"
                  style={{
                    background: 'var(--timer-button-primary)',
                    color: 'var(--colorsurface-50)',
                    boxShadow: '0 4px 12px rgba(31, 108, 79, 0.4)'
                  }}
                >
                  GO!
                </button>
              ) : waitingForRunStart ? (
                /* 🔧 FEB 13: "I'm Ready" gate — totalTime keeps ticking, but phases/GPS/voice wait */
                <button
                  onClick={startMainRun}
                  className="px-8 py-3 rounded-full text-base font-bold transition-all"
                  style={{
                    background: 'var(--timer-button-primary)',
                    color: 'var(--colorsurface-50)',
                    boxShadow: '0 4px 16px rgba(31, 108, 79, 0.5)'
                  }}
                >
                  I'm Ready, Let's Go!
                </button>
              ) : (
                <>
                  <button
                    onClick={isPaused ? resumeWorkout : pauseWorkout}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: isPaused ? 'var(--timer-button-primary)' : 'var(--timer-accent-warning)',
                      color: 'var(--colorsurface-50)'
                    }}
                  >
                    {isPaused ? '▶ Resume' : '⏸ Pause'}
                  </button>

                  <button
                    onClick={completeWorkout}
                    className="px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap"
                    style={{
                      background: 'var(--timer-accent-success)',
                      color: 'var(--colorsurface-50)',
                      minWidth: '90px'
                    }}
                  >
                    ✓ Finish
                  </button>

                  <button
                    onClick={discardWorkout}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: 'var(--timer-accent-danger)',
                      color: 'var(--colorsurface-50)'
                    }}
                  >
                    ✕ Discard
                  </button>

                  <button
                    onClick={restartWorkout}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: 'transparent',
                      color: 'var(--timer-accent-secondary)',
                      border: '1px solid var(--timer-accent-secondary)'
                    }}
                  >
                    ↻ Restart
                  </button>
                </>
              )}
            </>
          )}
          
          {/* COOLDOWN STAGE BUTTONS */}
          {currentStage === 'cooldown' && hasCooldown && (
            <>
              {!isRunning ? (
                <button
                  onClick={startWorkout}
                  className="px-8 py-3 rounded-full text-base font-bold transition-all"
                  style={{
                    background: 'var(--timer-button-primary)',
                    color: 'var(--colorsurface-50)',
                    boxShadow: '0 4px 12px rgba(31, 108, 79, 0.4)'
                  }}
                >
                  GO!
                </button>
              ) : (
                <>
                  <button
                    onClick={isPaused ? resumeWorkout : pauseWorkout}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: isPaused ? 'var(--timer-button-primary)' : 'var(--timer-accent-warning)',
                      color: 'var(--colorsurface-50)'
                    }}
                  >
                    {isPaused ? '▶ Resume' : '⏸ Pause'}
                  </button>
                  
                  <button
                    onClick={completeCooldown}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: 'var(--timer-accent-success)',
                      color: 'var(--colorsurface-50)'
                    }}
                  >
                    ✓ Complete
                  </button>
                  
                  <button
                    onClick={() => setShowSkipCooldownModal(true)}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      background: 'var(--timer-bg-tertiary)',
                      color: 'var(--timer-text-cedar)',
                      border: '1px solid var(--timer-border-medium)'
                    }}
                  >
                    ⏭️ Skip
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modals */}
      {showCompleteConfirm && (() => {
        // 🆕 PHASE 3.1: Use pre-computed cooldown detection (avoid duplicating logic)
        // 🔧 MAR 17, 2026: Only offer cooldown transition when it has actual content (hasCooldown checks both existence + content)
        const cooldown = hasCooldown ? cooldownMobility : null;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
              {/* 🆕 PHASE 3.2: Two-option screen if cooldown exists */}
              {cooldown ? (
                <>
                  <h3 className="text-lg font-semibold mb-4">Workout Complete! 🎉</h3>
                  <p className="text-gray-600 mb-2">
                    Great work! Ready for your cooldown?
                  </p>
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-6">
                    <p className="text-sm font-medium text-teal-900 mb-1">
                      {cooldown.title}
                    </p>
                    <p className="text-xs text-teal-700">
                      {cooldown.duration || cooldown.duration_minutes || '5-10'} minutes
                    </p>
                  </div>
                  <div className="space-y-3">
                    {/* Primary option: Continue to cooldown (stay in timer) */}
                    <button
                      onClick={() => {
                        setShowCompleteConfirm(false);
                        advanceToCooldown(); // 🔧 FEB 13: Transition to cooldown stage WITHIN the timer (not navigate away)
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg hover:from-teal-700 hover:to-emerald-700 font-medium shadow-sm"
                    >
                      ✓ Continue to Cooldown
                    </button>
                    
                    {/* Secondary option: Skip cooldown */}
                    <button
                      onClick={() => {
                        setCooldownToSkip(cooldown);
                        setShowCompleteConfirm(false);
                        setShowSkipCooldownModal(true);
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Skip Cooldown & Finish
                    </button>
                    
                    {/* Cancel option */}
                    <button
                      onClick={() => setShowCompleteConfirm(false)}
                      className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Original single-option modal (no cooldown) */}
                  <h3 className="text-lg font-semibold mb-4">Complete Workout?</h3>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to finish this workout? Your session data will be saved.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowCompleteConfirm(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmComplete}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Complete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {showDiscardConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px', maxWidth: '360px', margin: '0 16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px', textAlign: 'center' }}>Discard this session?</h3>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px', textAlign: 'center' }}>
              Your workout progress will not be saved.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={confirmDiscard}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: '#DC2626',
                  color: '#FFFFFF',
                }}
              >
                Discard
              </button>
              <button
                onClick={() => setShowDiscardConfirm(false)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                }}
              >
                Keep Going
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 FEB 4, 2026 (Multi-Stage): Skip Cooldown Reason Modal (New Multi-Stage Flow) */}
      {showSkipCooldownModal && currentStage === 'cooldown' && !cooldownToSkip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">Skip Cooldown?</h3>
            <p className="text-gray-600 mb-4">
              Why are you skipping today?
            </p>
            
            <div className="space-y-2 mb-6">
              <button
                onClick={() => handleSkipCooldownWithReason('not_enough_time')}
                className="w-full text-left border-2 border-gray-200 rounded-lg px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⏰</span>
                  <span className="text-sm font-medium">Not enough time</span>
                </div>
              </button>
              <button
                onClick={() => handleSkipCooldownWithReason('dont_need_it')}
                className="w-full text-left border-2 border-gray-200 rounded-lg px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🤷</span>
                  <span className="text-sm font-medium">Don't think I need it</span>
                </div>
              </button>
              <button
                onClick={() => handleSkipCooldownWithReason('forgot')}
                className="w-full text-left border-2 border-gray-200 rounded-lg px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😅</span>
                  <span className="text-sm font-medium">Just forgot</span>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setShowSkipCooldownModal(false)}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* 🆕 PHASE 3.3: Skip Cooldown Confirmation Modal (Legacy Flow - From Workout Completion) */}
      {showSkipCooldownModal && cooldownToSkip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">Skip Cooldown?</h3>
            <p className="text-gray-600 mb-4">
              Why are you skipping "{cooldownToSkip.title}"?
            </p>
            
            {/* Skip Reason Options */}
            <div className="space-y-2 mb-6">
              <button
                onClick={() => handleSkipCooldown('not_enough_time')}
                className="w-full px-4 py-3 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⏰</span>
                  <span className="text-sm font-medium">Not enough time</span>
                </div>
              </button>
              
              <button
                onClick={() => handleSkipCooldown('dont_need_it')}
                className="w-full px-4 py-3 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🤷</span>
                  <span className="text-sm font-medium">Don't think I need it</span>
                </div>
              </button>
              
              <button
                onClick={() => handleSkipCooldown('forgot_changed_mind')}
                className="w-full px-4 py-3 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😅</span>
                  <span className="text-sm font-medium">Just forgot / changed my mind</span>
                </div>
              </button>
              
              <button
                onClick={() => handleSkipCooldown('other')}
                className="w-full px-4 py-3 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">✏️</span>
                  <span className="text-sm font-medium">Other reason</span>
                </div>
              </button>
            </div>
            
            {/* Cancel button */}
            <button
              onClick={() => {
                setShowSkipCooldownModal(false);
                setCooldownToSkip(null);
                setShowCompleteConfirm(true); // Go back to previous modal
              }}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* 🆕 JAN 29, 2026: TASK 1.7 - Restart Confirmation Modal */}
      {showRestartConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">Restart Workout?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to restart this workout? All logged sets, reps, and timer data will be cleared. You'll start fresh from the beginning.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRestartConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRestart}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
              >
                ↻ Restart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 FEB 4, 2026 (Multi-Stage): Skip Cooldown Reason Modal */}
      {showSkipCooldownModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">Skip Cooldown?</h3>
            <p className="text-gray-600 mb-4">
              Why are you skipping today?
            </p>
            
            <div className="space-y-2 mb-6">
              <button
                onClick={() => handleSkipCooldownWithReason('not_enough_time')}
                className="w-full text-left border-2 border-gray-200 rounded-lg px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⏰</span>
                  <span className="text-sm font-medium">Not enough time</span>
                </div>
              </button>
              <button
                onClick={() => handleSkipCooldownWithReason('dont_need_it')}
                className="w-full text-left border-2 border-gray-200 rounded-lg px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🤷</span>
                  <span className="text-sm font-medium">Don't think I need it</span>
                </div>
              </button>
              <button
                onClick={() => handleSkipCooldownWithReason('forgot')}
                className="w-full text-left border-2 border-gray-200 rounded-lg px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😅</span>
                  <span className="text-sm font-medium">Just forgot</span>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setShowSkipCooldownModal(false)}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* 🏃‍♂️ RUN GUIDANCE OVERLAY - Personalized coaching tips for cardio phases */}
      {workoutType === 'cardio' && (
        <RunGuidanceOverlay
          phaseDuration={currentPhase?.duration_seconds || 0}
          phaseElapsed={phaseTime}
          coachingTips={currentPhase?.coaching_tips || []}
          isVisible={isRunning && !isPaused}
          isIntervalRepeat={!!(currentPhase?.expansion && currentPhase.expansion.rep > 1 && !currentPhase.expansion.isRecovery)}
        />
      )}
    </div>
  );
}; 